import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@nano/common/alert';
import { BankCodeTypeService, Classifier, CountryService, PaymentInstructionCodeService, ServiceChargeDetailsService } from '@nano/common/classifier';
import { Institution, InstitutionService } from '@nano/common/finance';
import { FormService } from '@nano/common/form';
import { ModalService, ModalSize, ModalVerticalDivider } from '@nano/common/modal';
import { NavService } from '@nano/common/nav';
import { PartyBeneficiary } from '@nano/common/party-beneficiaries';
import { CustomInputNumberParser, escapeHtml, indicateLoading, removeWhitespaces } from '@nano/common/util';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { AuthService, UserProfile } from '@nano/core/auth';
import { TfaUsage } from '@nano/core/tfa';
import { ScheduledPaymentPeriodType } from '@nano/scheduled-payments/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { fadeInOnEnterAnimation } from '@nano/common/animation';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { BehaviorSubject, combineLatest, EMPTY, iif, NEVER, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  pairwise,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  tap
} from 'rxjs/operators';

import {
  BankTransferControls,
  BankTransferCurrencyCloudAdditionalControls,
  BankTransferGatewayAdditionalControls,
  BankTransferInternationalControls,
  BankTransferScheduledControls,
  BankTransferSepaAdditionalBeneficiaryIdentificationControls,
  BankTransferSepaAdditionalControls,
  BankTransferSepaAdditionalInitialPayerIdentificationControls,
  BankTransferSepaAdditionalPayerIdentificationControls,
  BankTransferSepaAdditionalUltimateBeneficiaryIdentificationControls,
  BankTransferSwiftAdditionalControls,
  BankTransferSwiftXmlAdditionalControls,
  BankTransferSwiftXmlControls,
  BENEFICIARY_NAME_PART_FIELDS,
  appliesGatewayDefault,
  GATEWAY_EDITABLE_DEFAULT_FIELDS,
  GATEWAY_RESPONSE_ONLY_FIELDS,
  isControlFilled,
  isIdentificationInPlay,
  isShownForSelectedIdentification,
  normalizeGatewayConditions,
  BENEFICIARY_IDENTIFICATION_FIELDS,
  PAYER_IDENTIFICATION_FIELDS,
  PayeeVerificationInlineState,
  StructuredAddressConstants
} from './bank-transfer';
import { ApiErrorCode, ApiResponse } from '@nano/core/api';
import { PartyIdentificationType } from '@nano/common/party-identification';
import { Account, AccountService, isDemoAccount, PermittedOperationCurrencyType } from '@nano/account/common';
import { PaymentModalService } from '@nano/payment/payment-modal';
import {
  isPaymentBoa,
  isPaymentCanBeScheduled,
  isPaymentCurrencyCloud,
  isPaymentInternal,
  isPaymentSepa,
  isPaymentSwift,
  isPaymentSwiftXml,
  isPaymentTarget2,
  isPaymentGateway,
  Payment,
  PaymentAdditionalField,
  PaymentConditions,
  PaymentConfig,
  PaymentCurrencyCloudAdditionalParam,
  PaymentCustomField,
  PaymentDetailsType,
  PaymentFieldMetadata,
  PaymentInternationalParam,
  PaymentParam,
  PaymentScheduledParam,
  PaymentSepaAdditionalParam,
  PaymentSwiftAdditionalParam,
  PaymentSwiftXmlAdditionalParam,
  PaymentSwiftXmlParam,
  PaymentType,
  PaymentGatewayAdditionalParam,
  PaymentSystem,
  PayeeVerificationResponse,
  BeneficiaryMatchStatus,
  PayeeVerificationStatus,
  usesStructuredPaymentFields
} from '@nano/payment/payment';
import { PaymentService } from '@nano/payment/payment.service';
import { BeneficiaryAddressStatus } from '@nano/payment-additional-info';
import { SseService } from '@nano/core/sse';
import { SentryLastActionService } from '../../core/integration/sentry/sentry-last-action.service';

const PAYMENT_DETAILS_PREFILLED_KEY = 'payment.details.prefilled';

@UntilDestroy()
@Component({
  selector: 'app-bank-transfer',
  templateUrl: './bank-transfer.component.html',
  styleUrls: ['./bank-transfer.component.less'],
  animations: [fadeInOnEnterAnimation()],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BankTransferComponent implements OnInit {
  @ViewChild('alertRef', { read: ElementRef }) alertElement: ElementRef;

  @Input() payment?: Payment;

  readonly CustomInputNumberParser = CustomInputNumberParser;

  readonly countries$: Observable<Classifier[]> = this.countryService.worldwideCountries$;
  readonly instructionCodes$: Observable<Classifier[]> = this.paymentInstructionCodeService.instructionCodes$;
  private exchangeRateSseSubscription?: Subscription;

  readonly tfaUsage = TfaUsage.whenConfirmingOperation;
  readonly config: PaymentConfig = this.paymentService.config;

  readonly PayerIdentificationControls = BankTransferSepaAdditionalPayerIdentificationControls(this.config);
  readonly InitialPayerIdentificationControls = BankTransferSepaAdditionalInitialPayerIdentificationControls(this.config);
  readonly BeneficiaryIdentificationControls = BankTransferSepaAdditionalBeneficiaryIdentificationControls(this.config);
  readonly UltimateBeneficiaryIdentificationControls = BankTransferSepaAdditionalUltimateBeneficiaryIdentificationControls(this.config);

  readonly form: UntypedFormGroup = new UntypedFormGroup({});
  readonly indicateLoading$: Subject<boolean> = new Subject();
  readonly submitButtonDisabled$: Observable<boolean>;
  readonly submitButtonLoading$: Subject<boolean> = new Subject();

  readonly saveButtonDisabled$: Observable<boolean>;
  readonly saveButtonLoading$: Subject<boolean> = new Subject();
  readonly saveButtonVisible$: Observable<boolean>;
  private currencyDifference$ = new BehaviorSubject<boolean>(false);

  readonly displayExchangeAmount$: Observable<boolean>;
  private displayExchangeAmountSubject: BehaviorSubject<boolean>;

  readonly bankCodeTypes$: Observable<Classifier[]>;
  readonly serviceChargeDetails$: Observable<Classifier[]>;
  readonly PaymentIdentificationClasses = Object.values(PartyIdentificationType);

  readonly STRUCTURED_ADDRESS_CITY_MAX_LENGTH = StructuredAddressConstants.CITY_MAX_LENGTH;
  readonly STRUCTURED_ADDRESS_PROVINCE_MAX_LENGTH = StructuredAddressConstants.PROVINCE_MAX_LENGTH;
  readonly STRUCTURED_ADDRESS_STREET_NAME_MAX_LENGTH = StructuredAddressConstants.STREET_NAME_MAX_LENGTH;
  readonly STRUCTURED_ADDRESS_BUILDING_NUMBER_MAX_LENGTH = StructuredAddressConstants.BUILDING_NUMBER_MAX_LENGTH;
  readonly STRUCTURED_ADDRESS_BUILDING_NAME_MAX_LENGTH = StructuredAddressConstants.BUILDING_NAME_MAX_LENGTH;
  readonly STRUCTURED_ADDRESS_POSTAL_CODE_MAX_LENGTH = StructuredAddressConstants.POSTAL_CODE_MAX_LENGTH;

  private beneficiaryNameExtraSubject = new BehaviorSubject<string | null>(null);
  private storedPayeeVerificationId: string | null = null;
  private needsBeneficiaryValidation = true;
  private beneficiaryNameLockedByCloseMatch = false;
  private closeMatchResetArmed = false;
  userAgreementControl = new UntypedFormControl(false);
  readonly closeMatchAgreementControl = new UntypedFormControl({ value: false, disabled: true });
  showUserAgreementAlert = false;
  private closeMatchAgreementRequired = false;
  private vopValidationTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly VOP_INITIAL_TIMEOUT = 10000;
  private readonly BENEFICIARY_NAME_MAX_LENGTH_FALLBACK = 70;
  private vopBeneficiaryNameTemporarilyDisabled = false;
  private payeeVerificationInlineStateSubject = new BehaviorSubject<string>(PayeeVerificationInlineState.IDLE);
  readonly payeeVerificationInlineState$: Observable<string> = this.payeeVerificationInlineStateSubject.asObservable();
  readonly PayeeVerificationInlineState = PayeeVerificationInlineState;

  institutionValidating: boolean;
  institution: Institution;
  paymentConditions: PaymentConditions;
  userMustProvideInstitution: boolean;
  userMustProvideInstructionId: boolean;
  userCanProvideServiceChargeDetails: boolean;
  isPaymentDetailsStructured: boolean;
  isPaymentAddressStructured: boolean;
  isPaymentGatewayEnabled = false;
  isGatewayPaymentInitiated = false;
  showBeneficiaryCategory = false;
  addressInvalid = false;
  hasConditionsError = false;

  waitForFundsEnabled: boolean;
  feeAccountEnabled: boolean;
  feeAccountHasError: boolean;
  collapsed = false;

  attachmentsEnabled: boolean;
  attachments: NzUploadFile[];

  beneficiaryAddressFieldsMandatory: boolean;
  payerIdentificationMandatory: boolean;
  ultimateBeneficiaryIdentificationMandatory: boolean;
  beneficiaryIdentificationMandatory: boolean;

  beneficiaryAutocompleteEnabled!: boolean;
  beneficiaryAutocompleteMinLength!: number;
  beneficiaries$: Observable<PartyBeneficiary[]> = of([] as PartyBeneficiary[]);
  selectedBeneficiary: PartyBeneficiary;

  currencies: Classifier[];
  showReadOnlyCurrency = false;

  filesUploading: boolean;
  gatewayAdditionalControlNames: string[] = [];
  private gatewayPrefilledFields: PaymentAdditionalField[] = [];
  private gatewayRepeatedCurrencyExchange = false;
  private suspendedSchedule?: { frequency: ScheduledPaymentPeriodType; startDate: Date };
  private lastConditionMandatoryControls: AbstractControl[] = [];
  private allAccountCurrencies: Classifier[] = [];
  private branchBic?: string;
  private beneficiaryInstitutionResolved = false;
  private defaultGatewayCurrencies?: Classifier[];
  private gatewayControlsInitialized = false;
  private paymentAmountFocusState = {
    amount: false,
    exchangeAmount: false
  };

  private appConfig = inject(CONFIG);
  private userProfile?: UserProfile;
  private vopRecheckInitialized = false;
  private repeatedBeneficiaryIdentification?: string;

  private readonly sepaAdditionalControlNames = BankTransferSepaAdditionalControls(this.config).map((control) => control.name);
  private readonly swiftAdditionalControlNames = BankTransferSwiftAdditionalControls(this.config).map((control) => control.name);
  private readonly swiftXmlAdditionalControlNames = BankTransferSwiftXmlAdditionalControls(this.config).map((control) => control.name);
  private readonly bankTransferScheduledControlNames = BankTransferScheduledControls.map((control) => control.name);
  private readonly identificationControlNames = [
    ...this.PayerIdentificationControls,
    ...this.InitialPayerIdentificationControls,
    ...this.BeneficiaryIdentificationControls,
    ...this.UltimateBeneficiaryIdentificationControls
  ].map((control) => control.name);
  private readonly initialPayerIdentificationControlNames = this.InitialPayerIdentificationControls.map((control) => control.name);
  private readonly ultimateBeneficiaryIdentificationControlNames = this.UltimateBeneficiaryIdentificationControls.map((control) => control.name);
  private readonly identificationMainControlNames = [
    PaymentSepaAdditionalParam.payerIdentification,
    PaymentSepaAdditionalParam.initialPayerIdentification,
    PaymentSepaAdditionalParam.beneficiaryIdentification,
    PaymentSepaAdditionalParam.ultimateBeneficiaryIdentification
  ];
  readonly initialPayerAddressControlNames = [
    PaymentSwiftXmlAdditionalParam.initialPayerCountry,
    PaymentSwiftXmlAdditionalParam.initialPayerProvince,
    PaymentSwiftXmlAdditionalParam.initialPayerCity,
    PaymentSwiftXmlAdditionalParam.initialPayerStreetName,
    PaymentSwiftXmlAdditionalParam.initialPayerBuildingNumber,
    PaymentSwiftXmlAdditionalParam.initialPayerBuildingName,
    PaymentSwiftXmlAdditionalParam.initialPayerPostalCode
  ];
  readonly ultimateBeneficiaryAddressControlNames = [
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryCountry,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryProvince,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryCity,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryStreetName,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryBuildingNumber,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryBuildingName,
    PaymentSwiftXmlAdditionalParam.ultimateBeneficiaryPostalCode
  ];
  readonly swiftBeneficiaryAddressSectionControlNames = [PaymentSwiftAdditionalParam.beneficiaryAddress];
  readonly paymentIdentificationSectionControlNames = [PaymentSepaAdditionalParam.paymentIdentification, PaymentSepaAdditionalParam.sendPayerAddress];
  readonly swiftXmlPaymentIdentificationSectionControlNames = [PaymentSepaAdditionalParam.paymentIdentification];
  readonly payerIdentificationSectionControlNames = this.PayerIdentificationControls.map((control) => control.name);
  readonly initialPayerIdentificationSectionControlNames = this.InitialPayerIdentificationControls.map((control) => control.name);
  readonly beneficiaryAddressSectionControlNames = [
    PaymentSepaAdditionalParam.beneficiaryCountry,
    PaymentSepaAdditionalParam.beneficiaryAddress,
    PaymentSepaAdditionalParam.beneficiaryCity,
    PaymentSepaAdditionalParam.beneficiaryProvince,
    PaymentSepaAdditionalParam.beneficiaryStreetName,
    PaymentSepaAdditionalParam.beneficiaryBuildingNumber,
    PaymentSepaAdditionalParam.beneficiaryBuildingName,
    PaymentSepaAdditionalParam.beneficiaryPostalCode
  ];
  readonly beneficiaryIdentificationSectionControlNames = this.BeneficiaryIdentificationControls.map((control) => control.name);
  readonly ultimateBeneficiaryIdentificationSectionControlNames = this.UltimateBeneficiaryIdentificationControls.map((control) => control.name);
  readonly initialPayerSectionControlNames = [
    PaymentSepaAdditionalParam.initialPayerName,
    ...this.initialPayerIdentificationSectionControlNames,
    ...this.initialPayerAddressControlNames
  ];
  readonly ultimateBeneficiarySectionControlNames = [
    PaymentSepaAdditionalParam.ultimateBeneficiaryName,
    ...this.ultimateBeneficiaryIdentificationSectionControlNames,
    ...this.ultimateBeneficiaryAddressControlNames
  ];
  readonly additionalBankIdentificationSectionControlNames = [
    PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCodeType,
    PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCode
  ];
  readonly correspondentBankSectionControlNames = [
    PaymentSwiftXmlAdditionalParam.correspondentBankCode,
    PaymentSwiftXmlAdditionalParam.correspondentBankName,
    PaymentSwiftXmlAdditionalParam.correspondentBankCountry,
    PaymentSwiftXmlAdditionalParam.correspondentBankAddress,
    PaymentSwiftXmlAdditionalParam.accountWithBankCorrespondent,
    PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCodeType,
    PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCode
  ];
  private additionalInfoSectionManualState: Record<string, boolean | undefined> = {};
  private currencyCloudAdditionalControlNames: string[] = [];
  private currencyCloudCustomControlNames: string[] = [];
  private lastResolvedOperationType: PaymentType | null = null;
  private readonly startDate = new Date();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private countryService: CountryService,
    private paymentInstructionCodeService: PaymentInstructionCodeService,
    private translationService: L10nTranslationService,
    private navService: NavService,
    private bankCodeTypeService: BankCodeTypeService,
    private serviceChargeDetailsService: ServiceChargeDetailsService,
    private errorService: ErrorService,
    private paymentModalService: PaymentModalService,
    private alertService: AlertService,
    private formService: FormService,
    private modalService: ModalService,
    private accountService: AccountService,
    private paymentService: PaymentService,
    private institutionService: InstitutionService,
    private sseService: SseService,
    private authService: AuthService,
    private sentryLastActionService: SentryLastActionService
  ) {
    this.displayExchangeAmountSubject = new BehaviorSubject(false);
    this.displayExchangeAmount$ = this.displayExchangeAmountSubject.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.saveButtonVisible$ = this.navService.pendingPaymentsVisible$;
    this.bankCodeTypes$ = this.bankCodeTypeService.bankCodeTypes$;
    this.serviceChargeDetails$ = this.serviceChargeDetailsService.serviceChargeDetails$;
    this.submitButtonDisabled$ = this.indicateLoading$.pipe(
      map((isLoading) => this.feeAccountHasError || isLoading),
      startWith(true),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.saveButtonDisabled$ = this.indicateLoading$.pipe(
      map((isLoading) => this.feeAccountHasError || isLoading),
      startWith(true),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.attachmentsEnabled = this.appConfig.paymentAttachmentsEnabled;
    this.waitForFundsEnabled = this.appConfig.paymentWaitForFundsEnabled;
    this.feeAccountEnabled = this.appConfig.paymentFeeAccountEnabled;
    this.beneficiaryAutocompleteEnabled = this.appConfig.paymentBeneficiaryAutocompleteEnabled;
    this.beneficiaryAutocompleteMinLength = this.appConfig.paymentBeneficiaryAutocompleteMinLength;
    this.isPaymentGatewayEnabled = this.appConfig.paymentGatewayEnabled;
    this.formService.registerFormControls(this.form, BankTransferControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferScheduledControls);
    this.formService.registerFormControls(this.form, BankTransferSepaAdditionalControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSepaAdditionalPayerIdentificationControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSepaAdditionalInitialPayerIdentificationControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSepaAdditionalBeneficiaryIdentificationControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSepaAdditionalUltimateBeneficiaryIdentificationControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferInternationalControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSwiftAdditionalControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferSwiftXmlControls);
    this.formService.registerFormControls(this.form, BankTransferSwiftXmlAdditionalControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferCurrencyCloudAdditionalControls(this.config));
    this.formService.registerFormControls(this.form, BankTransferGatewayAdditionalControls());
    this.initUserAgreementValidation();
    this.initOperationTypeControl();
    this.initPayerAccountControl();
    this.initFeeAccountControl();
    this.initConditionsDependControls();
    this.initHasAdditionalInfoControl();
    if (this.appConfig.paymentGatewayEnabled) this.initGatewayControls();
  }

  private initBeneficiaryValidationRecheck(): void {
    setTimeout(() => {
      const recheck = () => {
        if (!this.needsBeneficiaryValidation) {
          this.needsBeneficiaryValidation = true;
          this.storedPayeeVerificationId = null;
        }
      };
      if (this.beneficiaryNameControl) {
        this.beneficiaryNameControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
          recheck();
          this.beneficiaryNameExtraSubject.next(null);
        });
      }
      if (this.beneficiaryAccountNumberControl) {
        this.beneficiaryAccountNumberControl.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
          recheck();
          this.beneficiaryNameExtraSubject.next(null);
        });
      }
    }, 0);
  }

  ngOnInit(): void {
    this.initVopProfileSubscription();
    this.form.markAsPristine();
    this.paymentService.paymentSummary$.pipe(take(1)).subscribe((payment) => {
      if (payment) {
        this.payment = this.payment ? ({ ...this.payment, ...payment } as Payment) : payment;
      }
    });

    this.gatewayRepeatedCurrencyExchange = this.isGatewayRepeatedCurrencyExchange();

    this.subscribeToExchangeRateSse();
    this.initIdentificationControlValueChanges();

    if (this.payment) {
      this.accountService.accounts$
        .pipe(
          take(1),
          map((accounts) =>
            accounts.find((account) => account.iban === this.payment.payerAccount?.iban && account.currency === this.payment.payerAccount?.currency)
          )
        )
        .subscribe((account) => {
          if (account) {
            this.payment.payerAccount.availableFunds = account.availableFunds;
          }
        });

      this.payerAccountControl.setValue(this.payment.payerAccount, { emitEvent: false });

      this.waitForFundsControl.setValue(this.waitForFundsEnabled && this.payment.payerAccount?.availableFunds <= 0);

      if (this.payment.scheduled) {
        this.formService.enableFormControls(this.form, this.bankTransferScheduledControlNames);
      }

      this.switchPaymentDetailsType(!!this.payment.paymentCode);

      this.userMustProvideInstitution = !!this.payment.beneficiaryBankIdentifier;
      this.formService.switchFormControlsEnabled(this.form, Object.values(PaymentInternationalParam), this.userMustProvideInstitution);

      const isRepeatedGatewayPayment = this.payment?.paymentSystem === PaymentSystem.PAYMENT_GATEWAY;
      this.holdRepeatedBeneficiaryIdentification();
      this.alignRepeatedBeneficiaryIdentificationCode();
      this.form.patchValue(this.payment);
      if (isRepeatedGatewayPayment && !this.payment.initialCurrency && this.payment.otherCurrency && this.payment.otherCurrency !== this.payment.currency) {
        this.initialCurrencyControl.setValue(this.payment.otherCurrency, { emitEvent: false });
      }
      if (isRepeatedGatewayPayment && !this.payment.initialAmount && this.payment.otherAmount) {
        this.initialAmountControl.setValue(this.payment.otherAmount, { emitEvent: false });
      }

      const paymentWithInstitution = this.payment as Payment & { institution?: Institution };
      const beneficiaryInstitution =
        paymentWithInstitution.institution ||
        (this.payment.beneficiaryBankFromClassifiers
          ? ({
              name: this.payment.beneficiaryBankName,
              code: this.payment.beneficiaryBankIdentifier,
              address: this.payment.beneficiaryBankAddress,
              country: this.payment.beneficiaryBankCountry,
              instructionIdentification: this.payment.instructionIdentification
            } as Institution)
          : null);

      if (beneficiaryInstitution) {
        this.applyBeneficiaryInstitution(beneficiaryInstitution);
      }

      this.resolveInitialBeneficiaryInstitutionFromAccountNumber(beneficiaryInstitution).pipe(take(1)).subscribe();

      if (this.enoughInfoToCheckConditions()) {
        const repeatPaymentPayload =
          isRepeatedGatewayPayment && this.payment?.payerAccount
            ? this.createGatewayPaymentConditionsRequest(this.payment.payerAccount, this.payment.currency, false)
            : undefined;
        this.getConditions(repeatPaymentPayload).pipe(take(1)).subscribe();
      }

      if (isRepeatedGatewayPayment || isPaymentGateway(this.payment.operationType)) {
        this.isGatewayPaymentInitiated = true;
        this.prefillGatewayCustomFieldsFromPayment();
      }

      this.attachments = this.payment.attachments;
    } else if (this.waitForFundsEnabled) {
      this.accountService.accounts$.pipe(take(1)).subscribe((accounts) => {
        if (accounts.length === 1 && !accounts[0].hasDebitAvailableFunds) {
          this.waitForFundsControl.setValue(true);
        }
      });
    } else {
      this.payment = {} as Payment;
    }

    this.prefillPaymentDetails();
    this.updateAvailableCurrencies();

    this.additionalBeneficiaryBankCodeTypeControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() =>
        this.formService.switchFormControlsRequired(
          [this.additionalBeneficiaryBankCodeTypeControl, this.additionalBeneficiaryBankCodeControl],
          !!this.additionalBeneficiaryBankCodeTypeControl.value
        )
      );

    this.additionalBeneficiaryCorrespondentBankCodeTypeControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() =>
        this.formService.switchFormControlsRequired(
          [this.additionalBeneficiaryCorrespondentBankCodeTypeControl, this.additionalBeneficiaryCorrespondentBankCodeControl],
          !!this.additionalBeneficiaryCorrespondentBankCodeTypeControl.value
        )
      );

    this.correspondentBankCodeControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe(() =>
        this.formService.switchFormControlsRequired(
          [this.correspondentBankCodeControl, this.correspondentBankNameControl, this.correspondentBankCountryControl, this.correspondentBankAddressControl],
          !!this.correspondentBankCodeControl.value
        )
      );

    this.displayExchangeAmount$.pipe(untilDestroyed(this)).subscribe((displayExchangeAmount) => {
      if (displayExchangeAmount) {
        this.waitForFundsControl.setValue(false, { emitEvent: false });
        this.waitForFundsControl.disable({ emitEvent: false });
      } else {
        this.waitForFundsControl.enable({ emitEvent: false });
      }
    });
  }

  private initVopProfileSubscription(): void {
    this.authService.userProfile$.pipe(untilDestroyed(this)).subscribe((profile) => {
      this.userProfile = profile ?? undefined;

      if (!this.appConfig.paymentGatewayEnabled) {
        this.isPaymentGatewayEnabled = !!profile?.canUsePaymentGatewayForOutgoingPayments;
        if (this.isPaymentGatewayEnabled && !this.gatewayControlsInitialized) {
          this.initGatewayControls();
        }
      }

      this.updateAvailableCurrencies();
    });

    this.authService.userProfile$
      .pipe(
        untilDestroyed(this),
        filter((profile) => !!profile && this.vopEnabled),
        take(1)
      )
      .subscribe(() => {
        this.initBeneficiaryValidationRecheck();
      });
  }

  private subscribeToExchangeRateSse(): void {
    this.exchangeRateSseSubscription?.unsubscribe();
    this.exchangeRateSseSubscription = this.sseService.exchangeRateChanged$.pipe(untilDestroyed(this)).subscribe(() => {
      const previousExchangeRate = this.payment?.exchangeRate;
      if (this.isGatewayPaymentInitiated)
        this.getConditions()
          .pipe(
            take(1),
            finalize(() => {
              const newExchangeRate = this.paymentConditions?.exchangeRate;
              if (newExchangeRate && previousExchangeRate !== undefined && newExchangeRate !== previousExchangeRate) {
                this.showExchangeRateAlert();
              }
            })
          )
          .subscribe();
    });
  }

  private showExchangeRateAlert(): void {
    this.alertService.alert({
      messageType: 'key',
      message: 'payment.exchangeRateChanged',
      icon: 'alert-circle',
      type: 'warning'
    });
  }

  handleBeneficiarySelected(beneficiary: PartyBeneficiary) {
    this.selectedBeneficiary = beneficiary;
    this.beneficiaryAccountNumberControl.setValue(this.selectedBeneficiary?.accountNumber);
    this.beneficiaryBankIdentifierControl.setValue(this.selectedBeneficiary?.bank);
    this.beneficiaryBankNameControl.setValue(this.selectedBeneficiary?.bankName);
    this.beneficiaryBankAddressControl.setValue(this.selectedBeneficiary?.bankAddress);
    this.beneficiaryBankCountryControl.setValue(this.selectedBeneficiary?.bankCountry);
    this.beneficiaryBankAddressControl.setValue(this.selectedBeneficiary?.instructionIdentification);
    this.beneficiaryProvinceControl.setValue(this.selectedBeneficiary?.beneficiaryProvince);
    this.beneficiaryPostalCodeControl.setValue(this.selectedBeneficiary?.beneficiaryPostalCode);
    this.beneficiaryCountryControl.setValue(this.selectedBeneficiary?.beneficiaryCountry);
    this.beneficiaryCityControl.setValue(this.selectedBeneficiary?.beneficiaryCity);
    this.beneficiaryStreetNameControl.setValue(this.selectedBeneficiary?.beneficiaryStreetName);
    this.beneficiaryBuildingNameControl.setValue(this.selectedBeneficiary?.beneficiaryBuildingName);
    this.beneficiaryBuildingNumberControl.setValue(this.selectedBeneficiary?.beneficiaryBuildingNumber);
    this.beneficiaryAddressControl.setValue(this.selectedBeneficiary?.beneficiaryAddressLine);
  }

  onInstitution(institution: Institution): void {
    this.institution = institution;
    this.beneficiaryInstitutionResolved = !!institution;
    this.beneficiaryBankIdentifierControl.setValue(institution?.code || null);
    this.beneficiaryBankNameControl.setValue(institution?.name || null);
    this.beneficiaryBankAddressControl.setValue(institution?.address || null);
    this.beneficiaryBankCountryControl.setValue(institution?.country || null);
    this.instructionIdentificationControl.setValue(institution?.instructionIdentification || null);
    this.updateAvailableCurrencies();
  }

  private applyBeneficiaryInstitution(institution: Institution): void {
    this.userMustProvideInstitution = false;
    this.institution = institution;
    this.beneficiaryInstitutionResolved = true;
    this.beneficiaryBankIdentifierControl.setValue(institution.code ?? this.payment?.beneficiaryBankIdentifier, { emitEvent: false });
    this.beneficiaryBankNameControl.setValue(institution.name ?? this.payment?.beneficiaryBankName, { emitEvent: false });
    this.beneficiaryBankAddressControl.setValue(institution.address ?? this.payment?.beneficiaryBankAddress, { emitEvent: false });
    this.beneficiaryBankCountryControl.setValue(institution.country ?? this.payment?.beneficiaryBankCountry, { emitEvent: false });
    this.instructionIdentificationControl.setValue(institution.instructionIdentification ?? this.payment?.instructionIdentification, { emitEvent: false });
    this.formService.disableFormControls(this.form, [
      PaymentInternationalParam.beneficiaryBankName,
      PaymentInternationalParam.beneficiaryBankAddress,
      PaymentInternationalParam.beneficiaryBankCountry,
      PaymentSwiftXmlParam.instructionIdentification
    ]);
    this.updateAvailableCurrencies();
  }

  private resolveInitialBeneficiaryInstitutionFromAccountNumber(existingInstitution: Institution | null): Observable<Institution | null> {
    if (existingInstitution || !this.payment?.beneficiaryAccountNumber) {
      return of(existingInstitution);
    }

    return this.institutionService.getInstitutions(this.payment.beneficiaryAccountNumber).pipe(
      take(1),
      map((response) => response.results?.[0] || null),
      catchError(() => of(null)),
      tap((institution) => {
        if (institution) {
          this.applyBeneficiaryInstitution(institution);
        }
      })
    );
  }

  onInstitutionValidating(institutionValidating: boolean): void {
    this.institutionValidating = institutionValidating;
  }

  onUserMustProvideInstitution(userMustProvideInstitution: boolean): void {
    if (this.userMustProvideInstitution !== userMustProvideInstitution) {
      this.userMustProvideInstitution = userMustProvideInstitution;
      this.formService.resetFormControls(this.form, Object.values(PaymentInternationalParam));
    }
    if (userMustProvideInstitution) {
      this.beneficiaryInstitutionResolved = false;
      this.showReadOnlyCurrency = false;
    }
    this.formService.switchFormControlsEnabled(this.form, Object.values(PaymentInternationalParam), userMustProvideInstitution);

    if (this.selectedBeneficiary) {
      if (this.selectedBeneficiary.bank) {
        this.beneficiaryBankIdentifierControl.setValue(this.selectedBeneficiary.bank ? this.institution?.code || this.selectedBeneficiary.bank : null);
      }
      if (this.selectedBeneficiary.bankName) {
        this.beneficiaryBankNameControl.setValue(this.institution?.name || this.selectedBeneficiary.bankName);
      }
      if (this.selectedBeneficiary.bankAddress) {
        this.beneficiaryBankAddressControl.setValue(this.institution?.address || this.selectedBeneficiary.bankAddress);
      }
      if (this.selectedBeneficiary.bankCountry) {
        this.beneficiaryBankCountryControl.setValue(this.institution?.country || this.selectedBeneficiary.bankCountry);
      }
      if (this.selectedBeneficiary.instructionIdentification) {
        this.instructionIdentificationControl.setValue(this.institution?.instructionIdentification || this.selectedBeneficiary.instructionIdentification);
      }
    }
    this.updateAvailableCurrencies();
  }

  onBeneficiaryBankInstitutionSelected(institution: Institution | null): void {
    this.institution = institution;
    this.beneficiaryInstitutionResolved = !!institution;
    this.updateAvailableCurrencies();
  }

  switchPaymentDetailsType(isPaymentDetailsStructured: boolean): void {
    this.isPaymentDetailsStructured = isPaymentDetailsStructured;
    if (isPaymentDetailsStructured) {
      this.paymentDetailsTypeControl.setValue(PaymentDetailsType.STRUCTURED);
      this.paymentCodeControl.setValue(this.paymentDetailsControl.value);
      this.paymentDetailsControl.disable();
      this.paymentCodeControl.enable();
    } else {
      this.paymentDetailsTypeControl.setValue(PaymentDetailsType.UNSTRUCTURED);
      this.paymentDetailsControl.setValue(this.paymentCodeControl.value);
      this.paymentCodeControl.disable();
      this.paymentDetailsControl.enable();
    }
  }

  switchScheduled(isScheduled: boolean): void {
    if ((isScheduled && !this.scheduledFrequency) || !isScheduled) {
      this.scheduledFrequencyControl.setValue(ScheduledPaymentPeriodType.DO_NOT_REPEAT);
      this.scheduledStartDateControl.setValue(new Date(this.startDate).setDate(new Date(this.startDate).getDate() + 1));
    }
    this.formService.switchFormControlsEnabled(this.form, Object.values(PaymentScheduledParam), isScheduled);
  }

  onInstitutionSelect(
    institution: Institution,
    institutionNameControlName: string,
    institutionAddressControlName: string,
    institutionCountryControlName?: string,
    instructionIdentificationControlName?: string
  ): void {
    if (institution) {
      this.form.get(institutionNameControlName).setValue(institution?.name);
      this.form.get(institutionAddressControlName).setValue(institution?.address);
      if (institutionCountryControlName) {
        this.form.get(institutionCountryControlName).setValue(institution?.country);
      }
      if (instructionIdentificationControlName) {
        this.form.get(instructionIdentificationControlName).setValue(institution?.instructionIdentification);
      }
    } else {
      this.form.get(institutionNameControlName).reset();
      this.form.get(institutionAddressControlName).reset();
      if (institutionCountryControlName) {
        this.form.get(institutionCountryControlName).reset();
      }
      if (instructionIdentificationControlName) {
        this.form.get(instructionIdentificationControlName).reset();
      }
    }

    if (!this.payment) {
      this.payment = {} as Payment;
    }

    if (this.attachments) {
      this.payment.attachments = this.attachments;
    }

    switch (institutionNameControlName) {
      case 'correspondentBankName':
        this.payment.intermediaryBankFromClassifiers = !!institution;
        break;
      case 'beneficiaryBankName':
        this.payment.beneficiaryBankFromClassifiers = !!institution;
        break;
    }

    this.formService.switchFormControlsEnabled(
      this.form,
      institutionCountryControlName
        ? [institutionNameControlName, institutionAddressControlName, institutionCountryControlName]
        : [institutionNameControlName, institutionAddressControlName],
      !institution
    );
  }

  onAttachmentsChange(attachments: NzUploadFile[]): void {
    this.attachments = attachments;
    this.form.markAsDirty();
    this.checkAndAlertBeneficiaryAddressFields();
    if (this.enoughInfoToCheckConditions()) {
      this.getConditions().pipe(take(1)).subscribe();
    }
  }

  makePayment(): void {
    this.sentryLastActionService.setPaymentAction('submit', this.paymentType);
    this.form.updateValueAndValidity();
    if (!this.paymentConditions || this.form.hasError('userAgreementRequired')) {
      return;
    }
    this.paymentConditions.institution = this.institution;
    this.syncGatewayCustomFieldValuesWithPaymentConditions();

    if (this.isPaymentSepa && this.needsBeneficiaryValidation && this.vopEnabled) {
      this.handleSepaValidationAndPayment();
    } else {
      this.proceedWithPayment();
    }
  }

  private proceedWithPayment(): void {
    if (this.beneficiaryAddressValidationIsRequired(this.paymentConditions)) {
      this.validateAndHandleBeneficiaryAddress();
    } else {
      this.paymentService
        .createPaymentOperation(this.formValueToPayment(), this.paymentConditions)
        .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe(() => {
          this.modalService.setStyle({ size: ModalSize.DEFAULT });
          this.cleanUpUserAgreementAlert();
        });
    }
  }

  private handleSepaValidationAndPayment(): void {
    this.startVopValidation();

    this.paymentService
      .validateSepaBeneficiaryName({
        name: this.beneficiaryNameControl.value as string,
        iban: this.beneficiaryAccountNumberControl.value as string,
        payeeAgentBankCode: this.beneficiaryBankIdentifierControl?.value as string
      })
      .pipe(
        indicateLoading(this.indicateLoading$),
        finalize(() => this.stopVopValidation())
      )
      .subscribe({
        next: (result) => {
          this.handlePayeeVerificationResult(result, () => this.proceedWithPayment());
        },
        error: () => {
          this.handlePayeeVerificationFailed();
        }
      });
  }

  private handleNoMatch(): void {
    this.clearCloseMatchAgreementRequirement();
    this.showUserAgreementAlert = true;
    this.userAgreementControl.enable();
    this.userAgreementControl.setValue(false);
    this.form.updateValueAndValidity({ emitEvent: false });
    this.needsBeneficiaryValidation = false;

    this.alertService.alert({
      messageType: 'key',
      message: 'payment.verificationOfPayee.noMatchTitle',
      messageHtml: true,
      icon: 'alert-circle',
      type: 'warning',
      agreementLabelKey: 'payment.verificationOfPayee.agreement'
    });

    this.userAgreementControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((checked) => {
      if (checked && this.showUserAgreementAlert) {
        this.beneficiaryNameControl.disable({ emitEvent: false });
        this.beneficiaryAccountNumberControl.disable({ emitEvent: false });
      } else {
        this.beneficiaryNameControl.enable({ emitEvent: false });
        this.beneficiaryAccountNumberControl.enable({ emitEvent: false });
      }
    });

    [this.beneficiaryNameControl, this.beneficiaryAccountNumberControl, this.payerAccountControl].forEach((control) => {
      control.valueChanges.pipe(untilDestroyed(this), take(1)).subscribe(() => this.cleanUpUserAgreementAlert());
    });
  }

  private handlePayeeVerificationFailed(): void {
    this.clearCloseMatchAgreementRequirement();
    this.showUserAgreementAlert = true;
    this.userAgreementControl.setValue(false);
    this.userAgreementControl.enable();
    this.form.updateValueAndValidity({ emitEvent: false });
    this.needsBeneficiaryValidation = false;

    this.alertService.alert({
      messageType: 'key',
      message: 'payment.verificationOfPayee.failedTitle',
      messageHtml: true,
      icon: 'alert-circle',
      type: 'warning',
      agreementLabelKey: 'payment.verificationOfPayee.agreement'
    });

    this.userAgreementControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((checked) => {
      if (checked && this.showUserAgreementAlert) {
        this.beneficiaryNameControl.disable({ emitEvent: false });
        this.beneficiaryAccountNumberControl.disable({ emitEvent: false });
      } else {
        this.beneficiaryNameControl.enable({ emitEvent: false });
        this.beneficiaryAccountNumberControl.enable({ emitEvent: false });
      }
    });

    [this.beneficiaryNameControl, this.beneficiaryAccountNumberControl, this.payerAccountControl].forEach((control) => {
      control.valueChanges.pipe(untilDestroyed(this), take(1)).subscribe(() => this.cleanUpUserAgreementAlert());
    });
  }

  private cleanUpUserAgreementAlert(): void {
    if (this.showUserAgreementAlert) {
      this.showUserAgreementAlert = false;
      this.userAgreementControl.disable();
      this.clearCloseMatchAgreementRequirement();
      this.form.updateValueAndValidity({ emitEvent: false });
      this.alertService.cleanUp();
    }
  }

  private initUserAgreementValidation(): void {
    this.form.addValidators(() => (this.closeMatchAgreementRequired && !this.closeMatchAgreementControl.value ? { userAgreementRequired: true } : null));

    this.closeMatchAgreementControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
      this.form.updateValueAndValidity({ emitEvent: false });
    });
  }

  private startVopValidation(): void {
    this.payeeVerificationInlineStateSubject.next(PayeeVerificationInlineState.VALIDATING);
    this.toggleBeneficiaryControlsForVopValidation(true);

    if (this.vopValidationTimer) {
      clearTimeout(this.vopValidationTimer);
    }

    this.vopValidationTimer = setTimeout(() => {
      if (this.payeeVerificationInlineStateSubject.value === PayeeVerificationInlineState.VALIDATING) {
        this.payeeVerificationInlineStateSubject.next(PayeeVerificationInlineState.TAKING_LONGER);
      }
    }, this.VOP_INITIAL_TIMEOUT);
  }

  private stopVopValidation(): void {
    this.payeeVerificationInlineStateSubject.next(PayeeVerificationInlineState.IDLE);
    this.toggleBeneficiaryControlsForVopValidation(false);

    if (this.vopValidationTimer) {
      clearTimeout(this.vopValidationTimer);
      this.vopValidationTimer = null;
    }
  }

  private toggleBeneficiaryControlsForVopValidation(disabled: boolean): void {
    if (disabled) {
      this.vopBeneficiaryNameTemporarilyDisabled = this.beneficiaryNameControl.enabled;

      if (this.vopBeneficiaryNameTemporarilyDisabled) {
        this.beneficiaryNameControl.disable({ emitEvent: false });
      }

      return;
    }

    if (this.vopBeneficiaryNameTemporarilyDisabled) {
      this.beneficiaryNameControl.enable({ emitEvent: false });
    }

    this.vopBeneficiaryNameTemporarilyDisabled = false;
  }

  private handleCloseMatch(matchedName: string): void {
    if (!matchedName?.trim()) {
      this.handlePayeeVerificationFailed();
      return;
    }

    const maxLength = this.config.beneficiaryNameMaxLength ?? this.BENEFICIARY_NAME_MAX_LENGTH_FALLBACK;
    const matchedNameFits = matchedName.length <= maxLength;

    if (matchedNameFits) {
      this.clearCloseMatchAgreementRequirement();
      this.unlockBeneficiaryNameAfterCloseMatch();
      this.beneficiaryNameControl.setValue(matchedName);
    } else {
      this.lockBeneficiaryNameAfterCloseMatch();
      this.requireCloseMatchAgreement();
    }

    this.closeMatchResetArmed = true;

    const messageKey = matchedNameFits ? 'payment.verificationOfPayee.closeMatchApplied' : 'payment.verificationOfPayee.closeMatchNameNotApplied';
    const extraKey = matchedNameFits ? 'payment.verificationOfPayee.closeMatchAppliedExtra' : 'payment.verificationOfPayee.closeMatchNameNotAppliedExtra';
    const messageParams = { name: matchedName };
    const alertMessageParams = { name: escapeHtml(matchedName) };

    this.beneficiaryNameExtraSubject.next(this.translationService.translate(extraKey, messageParams) as string);
    this.needsBeneficiaryValidation = false;

    this.alertService.alert({
      messageType: 'key',
      message: messageKey,
      messageParams: alertMessageParams,
      messageHtml: true,
      icon: 'alert-circle',
      type: 'warning',
      autoHide: false,
      ...(matchedNameFits ? {} : { agreementLabelKey: 'payment.verificationOfPayee.closeMatchAgreement' })
    });
  }

  private requireCloseMatchAgreement(): void {
    this.closeMatchAgreementRequired = true;
    this.showUserAgreementAlert = true;
    this.userAgreementControl.setValue(false, { emitEvent: false });
    this.closeMatchAgreementControl.enable({ emitEvent: false });
    this.closeMatchAgreementControl.setValue(false, { emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private clearCloseMatchAgreementRequirement(): void {
    if (!this.closeMatchAgreementRequired) {
      return;
    }

    this.closeMatchAgreementRequired = false;
    this.showUserAgreementAlert = false;
    this.closeMatchAgreementControl.disable({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private lockBeneficiaryNameAfterCloseMatch(): void {
    this.vopBeneficiaryNameTemporarilyDisabled = false;
    this.beneficiaryNameControl.disable({ emitEvent: false });
    this.beneficiaryNameLockedByCloseMatch = true;
  }

  private resetCloseMatchPresentation(): void {
    if (!this.closeMatchResetArmed) {
      return;
    }

    this.closeMatchResetArmed = false;
    this.beneficiaryNameExtraSubject.next(null);
    this.clearCloseMatchAgreementRequirement();
    this.unlockBeneficiaryNameAfterCloseMatch();
    this.alertService.cleanUp();
  }

  private unlockBeneficiaryNameAfterCloseMatch(): void {
    if (!this.beneficiaryNameLockedByCloseMatch) {
      return;
    }

    this.beneficiaryNameControl.enable({ emitEvent: false });
    this.beneficiaryNameLockedByCloseMatch = false;
  }

  private validateAndHandleBeneficiaryAddress(saveForLater?: boolean): void {
    this.paymentService
      .validateBeneficiaryAdress(this.formValueToPayment(), this.paymentConditions, saveForLater)
      .pipe(indicateLoading(this.submitButtonLoading$), indicateLoading(this.indicateLoading$))
      .subscribe((response) => {
        if (response?.status === BeneficiaryAddressStatus.VALID) {
          this.modalService.setStyle({ size: ModalSize.DEFAULT });
        } else {
          this.addressInvalid = true;
          this.alertService.alert({
            messageType: 'key',
            message: 'payment.beneficiaryAddressInvalid',
            icon: 'alert-circle',
            type: 'warning'
          });
          [
            this.beneficiaryCountryControl,
            this.beneficiaryProvinceControl,
            this.beneficiaryCityControl,
            this.beneficiaryPostalCodeControl,
            this.beneficiaryStreetNameControl,
            this.beneficiaryBuildingNumberControl,
            this.beneficiaryBuildingNameControl,
            this.beneficiaryAddressControl
          ]
            .filter((control) => !!control)
            .forEach((control) => {
              control.valueChanges.pipe(take(1)).subscribe(() => {
                this.addressInvalid = false;
                this.alertService.cleanUp();
              });
            });
        }
      });
  }

  save(): void {
    this.form.updateValueAndValidity();
    if (!this.paymentConditions || this.form.hasError('userAgreementRequired')) {
      return;
    }
    this.paymentConditions.institution = this.institution;
    this.syncGatewayCustomFieldValuesWithPaymentConditions();

    if (this.isPaymentSepa && this.vopEnabled && this.needsBeneficiaryValidation) {
      this.handleSepaValidationAndSave();
    } else {
      this.savePaymentForLater();
    }
  }

  private handleSepaValidationAndSave(): void {
    this.startVopValidation();

    this.paymentService
      .validateSepaBeneficiaryName({
        name: this.beneficiaryNameControl.value as string,
        iban: this.beneficiaryAccountNumberControl.value as string,
        payeeAgentBankCode: this.beneficiaryBankIdentifierControl?.value as string
      })
      .pipe(
        indicateLoading(this.indicateLoading$),
        finalize(() => this.stopVopValidation())
      )
      .subscribe({
        next: (result) => {
          this.handlePayeeVerificationResult(result, () => this.savePaymentForLater());
        },
        error: () => {
          this.handlePayeeVerificationFailed();
        }
      });
  }

  private handlePayeeVerificationResult(result: PayeeVerificationResponse | undefined, onMatch: () => void): void {
    if (!result) {
      return;
    }

    if (
      (result.request?.iban && result.request.iban !== removeWhitespaces(this.beneficiaryAccountNumberControl.value as string)?.toUpperCase()) ||
      (result.request?.name && result.request.name !== this.beneficiaryNameControl.value)
    ) {
      return;
    }

    const matchStatus = result?.matchingResponse?.partyNameMatch as BeneficiaryMatchStatus;
    const matchedName = result?.matchingResponse?.partyMatchedName;
    this.beneficiaryNameExtraSubject.next(null);
    this.storedPayeeVerificationId = result?.id;

    if (
      result.status === PayeeVerificationStatus.FAILED ||
      result.status === PayeeVerificationStatus.NOT_APPLICABLE ||
      matchStatus === BeneficiaryMatchStatus.NOT_APPLICABLE
    ) {
      this.handlePayeeVerificationFailed();
      return;
    }
    switch (matchStatus) {
      case BeneficiaryMatchStatus.MATCH:
        this.needsBeneficiaryValidation = false;
        onMatch();
        return;
      case BeneficiaryMatchStatus.CLOSE_MATCH:
        this.handleCloseMatch(matchedName);
        return;
      case BeneficiaryMatchStatus.NO_MATCH:
        this.handleNoMatch();
        return;
      default:
        this.needsBeneficiaryValidation = false;
        onMatch();
        return;
    }
  }

  private savePaymentForLater(): void {
    this.cleanUpUserAgreementAlert();
    if (this.beneficiaryAddressValidationIsRequired(this.paymentConditions)) {
      this.validateAndHandleBeneficiaryAddress(true);
    } else {
      this.paymentService
        .savePaymentForLaterConfirmation(this.formValueToPayment(), this.paymentConditions)
        .pipe(indicateLoading(this.saveButtonLoading$), indicateLoading(this.indicateLoading$))
        .subscribe(() => {
          this.modalService.setStyle({ size: ModalSize.DEFAULT });
          this.cleanUpUserAgreementAlert();
        });
    }
  }

  beneficiaryAddressValidationIsRequired(paymentConditions: PaymentConditions): boolean {
    return (
      !!paymentConditions?.restfulPayPaymentSystem &&
      !!paymentConditions.beneficiaryAddressValidationCountries?.includes(this.beneficiaryCountryControl.value as string)
    );
  }

  currencyCloudControlNameExists(controlName: string): boolean {
    return this.currencyCloudAdditionalControlNames.includes(controlName);
  }

  displayCurrencyCloudAddressLabel = (): boolean => {
    return this.currencyCloudAdditionalControlNames.some((control) =>
      [
        PaymentCurrencyCloudAdditionalParam.beneficiaryCountry,
        PaymentCurrencyCloudAdditionalParam.beneficiaryProvince,
        PaymentCurrencyCloudAdditionalParam.beneficiaryCity,
        PaymentCurrencyCloudAdditionalParam.beneficiaryAddress,
        PaymentCurrencyCloudAdditionalParam.beneficiaryPostalCode
      ].includes(control as PaymentCurrencyCloudAdditionalParam)
    );
  };

  canBeSaved = (): boolean => !this.scheduled;

  private initOperationTypeControl(): void {
    this.operationTypeControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap((operationType: PaymentType) => {
          if (isPaymentCanBeScheduled(operationType) && !this.scheduledControl.enabled) {
            this.resumeSchedule();
          } else if (!isPaymentCanBeScheduled(operationType) && !this.scheduledControl.disabled) {
            this.suspendSchedule();
          }
        })
      )
      .subscribe();
  }

  private suspendSchedule(): void {
    this.suspendedSchedule = this.scheduled ? { frequency: this.scheduledFrequency, startDate: this.scheduledStartDateControl?.value as Date } : undefined;
    this.scheduledControl.setValue(false);
    this.scheduledControl.disable();
  }

  private resumeSchedule(): void {
    this.scheduledControl.enable();
    if (!this.suspendedSchedule) {
      return;
    }

    const { frequency, startDate } = this.suspendedSchedule;
    this.suspendedSchedule = undefined;
    this.scheduledControl.setValue(true);
    this.scheduledFrequencyControl?.setValue(frequency);
    this.scheduledStartDateControl?.setValue(startDate);
  }

  private initPayerAccountControl(): void {
    this.payerAccountControl.valueChanges
      .pipe(
        untilDestroyed(this),
        map((account, index) => {
          if (!this.payment?.feeAccount || index > 1) {
            this.feeAccountControl.setValue(account);
          }
          return account;
        })
      )
      .subscribe((account: Account) => {
        const selectedCurrency = this.currencyControl.value as string;
        this.payerAccountNumberControl.setValue(account.iban);
        this.currencyControl.setValue(
          (this.isGatewayPaymentInitiated && this.currencyDifference$.getValue()) || this.payment?.paymentSystem == PaymentSystem.PAYMENT_GATEWAY
            ? selectedCurrency || this.payment?.currency || account.currency
            : account.currency
        );
      });
  }

  private initFeeAccountControl(): void {
    this.feeAccountControl.valueChanges.pipe(untilDestroyed(this)).subscribe((account: Account) => {
      this.feeAccountNumberControl.setValue(account.iban);
      this.feeAccountCurrencyControl.setValue(account.currency);
    });
  }

  private prefillPaymentDetails(): void {
    if (!this.payment?.paymentDetails && !this.payment?.paymentCode) {
      const paymentDetailsPrefilled = this.translationService.translate(PAYMENT_DETAILS_PREFILLED_KEY) as string;
      if (paymentDetailsPrefilled !== PAYMENT_DETAILS_PREFILLED_KEY && !this.isPaymentDetailsStructured) {
        this.paymentDetailsControl.setValue(paymentDetailsPrefilled);
      }
    }
  }

  private initGatewayControls(): void {
    if (this.gatewayControlsInitialized) {
      return;
    }
    this.gatewayControlsInitialized = true;

    this.accountService.accounts$.pipe(untilDestroyed(this)).subscribe((accounts) => {
      const realAccounts = accounts.filter((account) => !isDemoAccount(account));
      this.allAccountCurrencies = this.createCurrencyOptions(realAccounts.map((account) => account.currency));
      this.branchBic = realAccounts.find((account) => !!account?.branchBic)?.branchBic;
      this.defaultGatewayCurrencies = this.getGatewayCurrencies(realAccounts[0]);
      this.updateAvailableCurrencies();
    });

    this.payerAccountControl.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => this.updateAvailableCurrencies())
      )
      .subscribe();

    this.beneficiaryAccountNumberControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap(() => {
          this.beneficiaryInstitutionResolved = false;
          this.showReadOnlyCurrency = false;
          this.updateAvailableCurrencies();
        })
      )
      .subscribe();

    this.beneficiaryCategoryControl.valueChanges.pipe(startWith(false), untilDestroyed(this), distinctUntilChanged()).subscribe((value) => {
      if (this.isPaymentGatewayEnabled && this.enoughInfoToCheckConditions() && value) {
        this.getConditions().subscribe((payment) => {
          if (payment.operationType !== this.paymentType) {
            this.formService.resetFormControls(this.form, this.identificationControlNames);
            this.formService.clearValidatorsForControls(this.form, this.gatewayAdditionalControlNames);
          }
          const conditionsToPatch = { ...payment };
          delete conditionsToPatch.amount;
          delete conditionsToPatch.initialAmount;
          delete conditionsToPatch.operationType;
          this.form.patchValue(conditionsToPatch);
        });
      }
    });

    this.currencyControl.valueChanges.pipe(startWith(false), untilDestroyed(this)).subscribe(() => {
      const payerAccount = this.payerAccountControl.value;
      if (payerAccount) {
        const payerCurrency = payerAccount.currency;
        const canUseGatewayCurrencySelection = this.isPaymentGatewayCurrencySelectionAllowed(payerAccount);

        if (!canUseGatewayCurrencySelection && this.currencyControl.value !== payerCurrency && payerAccount.paymentGatewayEligiblePayerCurrencies) {
          this.currencyControl.setValue(payerCurrency, { emitEvent: false });
        }

        const wasDifferentCurrency = this.currencyDifference$.getValue();
        this.currencyDifference$.next(canUseGatewayCurrencySelection && this.currencyControl.value !== payerCurrency);
        const isDifferentCurrency = this.currencyDifference$.getValue();
        const currencyNoLongerDifferent = wasDifferentCurrency && !isDifferentCurrency;

        this.initialCurrencyControl.setValue(isDifferentCurrency ? payerCurrency : null, { emitEvent: false });
        this.initialCurrencyControl[isDifferentCurrency ? 'enable' : 'disable']();
        this.initialAmountControl[isDifferentCurrency ? 'enable' : 'disable']({ emitEvent: false });

        if (!isDifferentCurrency && this.isGatewayPaymentInitiated) {
          this.displayExchangeAmountSubject.next(false);
        }

        if (currencyNoLongerDifferent && this.isGatewayPaymentInitiated) {
          this.formService.clearValidatorsForControls(this.form, [PaymentGatewayAdditionalParam.beneficiaryCategory]);
          const customFieldsGroup = this.form.get(PaymentGatewayAdditionalParam.customFields) as FormGroup;
          if (customFieldsGroup) {
            this.formService.resetFormControls(customFieldsGroup, Object.keys(customFieldsGroup.controls));
            this.formService.clearValidatorsForControls(customFieldsGroup, Object.keys(customFieldsGroup.controls));
          }
        }

        if (isDifferentCurrency) {
          if (!this.payment) {
            this.payment = {} as Payment;
          }
          this.payment.currency = this.currencyControl.value as string;
          this.isGatewayPaymentInitiated = true;
          this.operationTypeControl.setValue(null);
          if (this.enoughInfoToCheckConditions()) {
            this.getConditions().pipe(take(1)).subscribe();
          }
        } else if (currencyNoLongerDifferent || (this.paymentConditions && !isPaymentGateway(this.paymentConditions.operationType))) {
          this.isGatewayPaymentInitiated = false;
        }
      }
    });

    this.createGatewayConditionsObservable([this.payerAccountControl, this.initialAmountControl], true).pipe(startWith(false)).subscribe();

    this.subscribePayerIdentificationCodeTypeValueChanges();
  }

  private subscribePayerIdentificationCodeTypeValueChanges(): void {
    const control = this.form.get(PaymentSepaAdditionalParam.payerIdentificationCodeType);
    if (!control) {
      return;
    }

    control.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(control.value as string),
        distinctUntilChanged(),
        skip(1),
        debounceTime(300),
        filter(() => this.isGatewayPaymentInitiated && this.enoughInfoToCheckConditions()),
        switchMap(() => this.getConditions())
      )
      .subscribe();
  }

  private updateAvailableCurrencies(): void {
    if (!this.gatewayControlsInitialized) {
      return;
    }

    const payerAccount = this.payerAccountControl.value;
    if (!payerAccount) {
      this.showReadOnlyCurrency = false;
      this.currencies = this.defaultGatewayCurrencies;
      return;
    }
    const payerCurrency = payerAccount.currency;
    if (!this.isPaymentGatewayCurrencySelectionAllowed(payerAccount)) {
      this.showReadOnlyCurrency = false;
      this.currencies = this.createCurrencyOptions([payerCurrency]);
      return;
    }

    const gatewayCurrencies = this.getGatewayCurrencies(payerAccount) || [];
    const repeatedPaymentCurrency = this.getRepeatedPaymentCurrency(payerAccount);
    const repeatedInternalCurrency = this.getRepeatedInternalReadOnlyCurrency(payerAccount);
    const repeatedPaymentCurrencyMissingFromGateway = !!repeatedPaymentCurrency && !gatewayCurrencies.some(({ id }) => id === repeatedPaymentCurrency);

    if (repeatedInternalCurrency) {
      this.showReadOnlyCurrency = true;
      this.currencies = this.createCurrencyOptions([repeatedInternalCurrency]);
      if (this.currency !== repeatedInternalCurrency) {
        this.currencyControl.setValue(repeatedInternalCurrency);
      }
      return;
    }

    if (!this.beneficiaryInstitutionResolved) {
      this.showReadOnlyCurrency = false;
      this.currencies = this.createCurrencyOptions([
        payerCurrency,
        ...(repeatedPaymentCurrencyMissingFromGateway ? [repeatedPaymentCurrency] : []),
        ...gatewayCurrencies
      ]);

      if (!this.currency || !this.currencies?.some(({ id }) => id === this.currency)) {
        this.currencyControl.setValue(repeatedPaymentCurrency || payerCurrency);
      }
      return;
    }

    const isInternalBeneficiary = this.isInternalBeneficiary(payerAccount);
    this.showReadOnlyCurrency = isInternalBeneficiary;
    this.currencies = isInternalBeneficiary
      ? this.createCurrencyOptions([payerCurrency])
      : this.getExternalBeneficiaryCurrencies(payerAccount, repeatedPaymentCurrency);

    if (isInternalBeneficiary) {
      if (this.currency !== payerCurrency) {
        this.currencyControl.setValue(payerCurrency);
      }
      return;
    }

    if (!this.currency || !this.currencies.some(({ id }) => id === this.currency)) {
      this.currencyControl.setValue(repeatedPaymentCurrency || payerCurrency);
    }
  }

  private getGatewayCurrencies(account?: Account): Classifier[] | undefined {
    return account?.permittedOperationCurrencies?.get(PermittedOperationCurrencyType.OUTGOING_PAYMENTS);
  }

  private getExternalBeneficiaryCurrencies(payerAccount: Account, repeatedPaymentCurrency: string | null): Classifier[] {
    const payerCurrency = payerAccount.currency;
    const gatewayCurrencies = this.getGatewayCurrencies(payerAccount) || [];
    const repeatedPaymentCurrencyMissingFromGateway = !!repeatedPaymentCurrency && !gatewayCurrencies.some(({ id }) => id === repeatedPaymentCurrency);

    return this.createCurrencyOptions([
      payerCurrency,
      ...(repeatedPaymentCurrencyMissingFromGateway ? [repeatedPaymentCurrency] : []),
      ...(gatewayCurrencies.length ? gatewayCurrencies : this.allAccountCurrencies)
    ]);
  }

  private isPaymentGatewayPayerCurrencyEligible(account?: Account | null): boolean {
    return !!account?.paymentGatewayEligiblePayerCurrencies?.includes(account.currency);
  }

  private get usesVisaCapabilityGatewayFlow(): boolean {
    return !this.appConfig.paymentGatewayEnabled && !!this.userProfile?.canUsePaymentGatewayForOutgoingPayments;
  }

  private isPaymentGatewayCurrencySelectionAllowed(account?: Account | null): boolean {
    return !!account && this.isPaymentGatewayEnabled && (!this.usesVisaCapabilityGatewayFlow || this.isPaymentGatewayPayerCurrencyEligible(account));
  }

  private getRepeatedPaymentCurrency(payerAccount: Account): string | null {
    const repeatedPaymentCurrency = this.payment?.currency;
    const repeatedPaymentPayerAccountIban = this.payment?.payerAccount?.iban || this.payment?.payerAccountNumber;

    if (!this.payment?.operationId || !repeatedPaymentCurrency || repeatedPaymentPayerAccountIban !== payerAccount.iban) {
      return null;
    }

    return repeatedPaymentCurrency;
  }

  private getRepeatedInternalReadOnlyCurrency(payerAccount: Account): string | null {
    const repeatedPaymentCurrency = this.getRepeatedPaymentCurrency(payerAccount);
    const branchBic = this.normalizeBic(payerAccount.branchBic || this.branchBic);
    const beneficiaryBic = this.normalizeBic(this.payment?.beneficiaryBankIdentifier || (this.beneficiaryBankIdentifierControl.value as string));

    if (!repeatedPaymentCurrency || repeatedPaymentCurrency !== payerAccount.currency || !branchBic || branchBic !== beneficiaryBic) {
      return null;
    }

    return repeatedPaymentCurrency;
  }

  private isInternalBeneficiary(payerAccount: Account): boolean {
    const branchBic = this.normalizeBic(payerAccount.branchBic || this.branchBic);
    const beneficiaryBic = this.normalizeBic(this.beneficiaryBankIdentifierControl.value as string);
    return !!branchBic && branchBic === beneficiaryBic;
  }

  private createCurrencyOptions(currencies: Array<string | Classifier | undefined>): Classifier[] {
    const currencyOptions = new Map<string, Classifier>();
    currencies
      .filter((currency): currency is string | Classifier => !!currency)
      .forEach((currency) => {
        if (typeof currency === 'string') {
          if (!currencyOptions.has(currency)) {
            currencyOptions.set(currency, { id: currency, label: currency });
          }
          return;
        }

        const existingCurrency = currencyOptions.get(currency.id);
        if (!existingCurrency || existingCurrency.label === existingCurrency.id) {
          currencyOptions.set(currency.id, currency);
        }
      });
    return Array.from(currencyOptions.values());
  }

  private normalizeBic(value?: string | null): string | null {
    const bic = value?.trim().toUpperCase();
    return bic || null;
  }

  onPaymentAmountFocusChange(control: 'amount' | 'exchangeAmount', focused: boolean): void {
    this.paymentAmountFocusState[control] = focused;
  }

  private patchConditionsAmountControl(control: AbstractControl, value: number | null | undefined, isFocused: boolean): void {
    if (value == null || isFocused) {
      return;
    }

    control.patchValue(value, { emitEvent: false });
  }

  private syncGatewayAmountsFromConditions(conditions: PaymentConditions): void {
    this.patchConditionsAmountControl(this.initialAmountControl, conditions.initialAmount, this.paymentAmountFocusState.exchangeAmount);
    this.patchConditionsAmountControl(this.amountControl, conditions.amount, this.paymentAmountFocusState.amount);
  }

  private createGatewayConditionsObservable(controls: AbstractControl[], isInitialAmountControl: boolean): Observable<PaymentConditions> {
    return combineLatest(controls.map((control) => control.valueChanges.pipe(distinctUntilChanged()))).pipe(
      debounceTime(1000),
      switchMap(() => {
        const isDifferentCurrency = this.currencyDifference$.getValue();
        if (this.isGatewayPaymentInitiated && isDifferentCurrency) {
          return this.getConditions(this.createGatewayPaymentConditionsRequest(this.payerAccountControl.value, this.currency, isInitialAmountControl));
        }
        return EMPTY;
      })
    );
  }

  private createGatewayPaymentConditionsRequest(payerAccount: Account, currency: string, isInitialAmountControl: boolean): Payment {
    const shouldUseInitialAmount = (isInitialAmountControl && this.initialAmount) || (!isInitialAmountControl && !this.amount);
    const errorControl = shouldUseInitialAmount ? this.amountControl : this.initialAmountControl;
    errorControl.setErrors(null);
    const repeatedGatewayInitialCurrency = this.gatewayRepeatedCurrencyExchange ? this.payment?.otherCurrency : undefined;
    const repeatedGatewayInitialAmount = this.gatewayRepeatedCurrencyExchange ? this.payment?.otherAmount : undefined;

    const payload = this.formValueToConditionsPayload();
    return {
      ...payload,
      payerAccount,
      payerAccountNumber: payerAccount?.iban,
      initialAmount: shouldUseInitialAmount ? this.initialAmount || repeatedGatewayInitialAmount : null,
      initialCurrency: this.initialCurrency || repeatedGatewayInitialCurrency,
      ...((payload.otherAmount || repeatedGatewayInitialAmount) && { otherAmount: payload.otherAmount || repeatedGatewayInitialAmount }),
      ...((payload.otherCurrency || repeatedGatewayInitialCurrency) && { otherCurrency: payload.otherCurrency || repeatedGatewayInitialCurrency }),
      amount: shouldUseInitialAmount ? null : this.amount,
      beneficiaryBankIdentifier: this.beneficiaryBankIdentifierControl?.value as string,
      currency
    };
  }

  private initConditionsDependControls(): void {
    const emptyConditions$ = of({} as PaymentConditions);

    const conditionsDependControlsValueChanges$ = combineLatest([
      this.payerAccountControl.valueChanges,
      this.waitForFundsControl.valueChanges.pipe(startWith(false)),
      this.feeAccountControl.valueChanges,
      this.beneficiaryAccountNumberControl.valueChanges,
      this.beneficiaryBankIdentifierControl.valueChanges,
      this.amountControl.valueChanges.pipe(
        tap(() => this.indicateLoading$.next(true)),
        delay(1000)
      ),
      this.beneficiaryCountryControl.valueChanges.pipe(startWith(this.beneficiaryCountryControl.value as string), distinctUntilChanged()),
      this.beneficiaryBankCountryControl.valueChanges,
      this.currencyControl.valueChanges.pipe(distinctUntilChanged()),
      this.scheduledControl.valueChanges.pipe(
        startWith(false),
        distinctUntilChanged(),
        tap((scheduled: boolean) => this.switchScheduled(scheduled))
      )
    ]).pipe(debounceTime(300));

    conditionsDependControlsValueChanges$
      .pipe(
        untilDestroyed(this),
        tap(() => this.cleanUpConditionsErrorAlert()),
        mergeMap(() => iif(() => this.enoughInfoToCheckConditions(), this.getConditions(), emptyConditions$))
      )
      .subscribe();
  }

  private enoughInfoToCheckConditions(): boolean {
    this.checkAndAlertBeneficiaryAddressFields();
    const enough =
      this.formService.isFormControlsEnabledAndValid([this.beneficiaryAccountNumberControl, this.beneficiaryBankIdentifierControl, this.payerAccountControl]) &&
      !!this.amountControl.value &&
      !this.institutionValidating;
    return enough;
  }

  private scrollToAlert() {
    if (this.alertElement) {
      // DEV-24729, DEV-27629: Scroll to alert for "Payments to this institution are not allowed"
      // Ensures user instantly sees the message by scrolling to the alert element when this error occurs.
      // eslint-disable-next-line @rx-angular/prefer-no-layout-sensitive-apis, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.alertElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private cleanUpConditionsErrorAlert(): void {
    if (!this.hasConditionsError && !this.form.hasError('apiError')) {
      return;
    }

    this.hasConditionsError = false;

    if (this.form.hasError('apiError')) {
      const currentErrors = { ...(this.form.errors || {}) };
      delete currentErrors.apiError;
      this.form.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }

    if (!this.showUserAgreementAlert) {
      this.alertService.cleanUp();
    }
  }

  private getConditions(payload?: Payment): Observable<PaymentConditions> {
    this.resetCloseMatchPresentation();

    return this.paymentService.getPaymentConditions(payload ? payload : this.formValueToConditionsPayload()).pipe(
      indicateLoading(this.indicateLoading$),
      catchError((httpError: HttpErrorResponse) => {
        const error = httpError.error as ApiResponse;
        const errorCode = error.metadata?.messages?.[0]?.code;
        this.hasConditionsError = true;
        const promptsForBeneficiaryCategory = (errorCode as ApiErrorCode) === ApiErrorCode.beneficiaryCategoryRequired && this.isPaymentGatewayEnabled;
        if (promptsForBeneficiaryCategory) {
          this.operationTypeControl.setValue(null);
          this.operationTypeControl.setValue(null);
          this.hasAdditionalInfoControl.setValue(true);
          this.hasAdditionalInfoControl.disable();
          this.beneficiaryCategoryControl.setValidators([Validators.required]);
          this.beneficiaryCategoryControl.enable();
          this.showBeneficiaryCategory = true;
          this.isGatewayPaymentInitiated = true;
          this.modalService.setStyle({ size: ModalSize.LG, divider: ModalVerticalDivider.half });
          setTimeout(() => {
            this.alertService.alert({
              messageType: 'key',
              message: this.translationService.translate('validation.beneficiaryCategoryRequired') as string,
              icon: 'alert-circle',
              type: 'warning'
            });
          });
        } else if ((errorCode as ApiErrorCode) === ApiErrorCode.paymentToInstitutionIsNotAllowed) {
          this.scrollToAlert();
        }
        if (this.showUserAgreementAlert && !this.isPaymentSepa) {
          this.cleanUpUserAgreementAlert();
        }
        if (!promptsForBeneficiaryCategory) {
          this.errorService.handleHttpError(httpError);
        }
        return NEVER;
      }),

      map((conditions) => normalizeGatewayConditions({ ...conditions })),

      tap((conditions) => {
        this.cleanUpConditionsErrorAlert();
        if (conditions.operationType !== this.paymentType) {
          this.operationTypeControl.setValue(conditions?.operationType);
          this.hasAdditionalInfoControl.enable();

          this.resetFieldsMetadata();
        }
        this.setPaymentDetailsValidators(conditions?.operationType);
        this.feeAccountHasError = !!conditions?.feeAccountError;
        this.initServiceChargeDetails(conditions);
        this.initFieldsMetadata(conditions.fieldsMetadata);

        if (isPaymentCurrencyCloud(conditions?.operationType)) {
          this.initCurrencyCloudAdditionalControls(conditions.additionalFields);
          this.initCurrencyCloudCustomControls(conditions.customFields);
          this.hasAdditionalInfoControl.setValue(true);
          this.hasAdditionalInfoControl.disable();
          this.beneficiaryIdentification?.updateValueAndValidity();
        }

        if (this.isPaymentGatewayEnabled) {
          this.syncGatewayAmountsFromConditions(conditions);

          if (isPaymentGateway(conditions?.operationType)) {
            this.initGatewayAdditionalControls(conditions);
            this.updateGatewayPrefilledFields(conditions.additionalFields);
            this.prefillGatewayCustomFieldsFromPayment();
            this.hasAdditionalInfoControl.setValue(true);
            this.hasAdditionalInfoControl.disable();
            this.isGatewayPaymentInitiated = true;
            this.displayExchangeAmountSubject.next(this.currencyControl.value !== this.payerAccountControl.value.currency);
          } else {
            this.isGatewayPaymentInitiated = false;
            this.gatewayPrefilledFields = [];
            this.beneficiaryCategoryControl.disable();
          }
        }

        this.paymentConditions = conditions;
        this.isPaymentAddressStructured = conditions.structuredAddressEnabled;
        this.paymentDetailsControl.updateValueAndValidity();

        this.initInstructionIdentificationControls();
        this.initBeneficairyIdentificationControl(conditions);

        this.handleBeneficiaryAddressFields(conditions);
        this.markAllAsTouched();
      }),
      delay(100),
      finalize(() => {
        if (!(this.showUserAgreementAlert && this.isPaymentSepa)) {
          this.alertService.cleanUp();
        }
        this.checkAndAlertBeneficiaryAddressFields();
      })
    );
  }

  private initBeneficairyIdentificationControl(conditions: PaymentConditions): void {
    const category = this.beneficiaryCategoryControl.value as string;
    const isMandatory = !!conditions?.additionalFields?.find(
      ({ name, mandatory }) => (name as PaymentCurrencyCloudAdditionalParam) === PaymentCurrencyCloudAdditionalParam.beneficiaryIdentification && mandatory
    );
    const isGatewayPayment = isPaymentGateway(conditions?.operationType);
    if (category && (isMandatory || isGatewayPayment) && !this.keepsRepeatedBeneficiaryIdentification(category)) {
      this.form.get(PaymentSepaAdditionalParam.beneficiaryIdentification).setValue(category);
    }
  }

  private alignRepeatedBeneficiaryIdentificationCode(): void {
    if (!this.payment?.beneficiaryIdentificationCode || this.payment?.beneficiaryId) {
      return;
    }

    this.payment.beneficiaryId = this.payment.beneficiaryIdentificationCode;
  }

  private holdRepeatedBeneficiaryIdentification(): void {
    const repeated = this.payment?.beneficiaryIdentification;
    const category = this.payment?.beneficiaryCategory;
    if (!repeated || !category || repeated === category) {
      return;
    }

    this.repeatedBeneficiaryIdentification = repeated;
    this.beneficiaryCategoryControl.valueChanges
      .pipe(
        filter((value) => value !== category),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => (this.repeatedBeneficiaryIdentification = undefined));
  }

  private keepsRepeatedBeneficiaryIdentification(category: string): boolean {
    return !!this.repeatedBeneficiaryIdentification && category === this.payment?.beneficiaryCategory;
  }

  private isGatewayRepeatedCurrencyExchange(): boolean {
    return this.payment?.paymentSystem === PaymentSystem.PAYMENT_GATEWAY && this.payment?.otherCurrency !== this.payment?.currency;
  }

  private updateGatewayPrefilledFields(additionalFields: PaymentAdditionalField[] = []): void {
    this.gatewayPrefilledFields = additionalFields
      .filter((field) => field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '')
      .filter((field) => !GATEWAY_EDITABLE_DEFAULT_FIELDS.includes(field.name))
      .filter((field) => appliesGatewayDefault(field, additionalFields));
  }

  private withoutGatewayServerLockedControls(controlNames: string[]): string[] {
    const gatewayServerLocked = new Set(this.gatewayPrefilledFields.map((field) => field.name));
    return controlNames.filter((name) => !gatewayServerLocked.has(name));
  }

  get gatewayPaymentConditions(): PaymentConditions | undefined {
    return isPaymentGateway(this.paymentConditions?.operationType) ? this.paymentConditions : undefined;
  }

  get gatewayPrefilledControls(): UntypedFormControl[] {
    return this.gatewayPrefilledFields.map((field) => this.form.get(field.name) as UntypedFormControl).filter(Boolean);
  }

  get allowedBeneficiaryIdentificationValues(): string[] {
    const category = this.beneficiaryCategoryControl.value as string;
    return [category].filter((v) => v !== undefined && v !== null);
  }

  get allowedPayerIdentificationValues(): string[] {
    const defaultValue = this.gatewayFieldDefault(PaymentSepaAdditionalParam.payerIdentification);
    return defaultValue ? [defaultValue] : [];
  }

  get payerIdentificationTypeDefault(): string | null {
    return this.gatewayFieldDefault(PaymentSepaAdditionalParam.payerIdentificationType) ?? null;
  }

  private gatewayFieldDefault(fieldName: string): string | undefined {
    return this.gatewayPaymentConditions?.additionalFields?.find(({ name }) => name === fieldName)?.defaultValue;
  }

  private handleBeneficiaryAddressFields(conditions: PaymentConditions): void {
    if (this.isPaymentAddressStructured && (this.selectedBeneficiary || this.beneficiaryAddressControl.value)) {
      this.beneficiaryAddressControl.reset();
    }

    if (!(this.isPaymentSepa || this.isPaymentSwiftXml || this.isPaymentInternal)) return;

    if (conditions.structuredAddressEnabled) {
      this.clearBeneficiaryAddressControl();
    } else if (this.beneficiaryAddressFieldsMandatory) {
      this.clearBeneficiaryAddressFields();
    } else if (this.payment?.beneficiaryCountry && this.payment?.beneficiaryCity) {
      this.clearBeneficiaryCityFields();
    } else if (this.selectedBeneficiary) {
      this.clearBeneficiaryAddressFields();
      this.clearBeneficiaryCityFields();
    }
  }

  private clearBeneficiaryAddressFields(): void {
    this.beneficiaryStreetNameControl.setValue(null);
    this.beneficiaryBuildingNumberControl.setValue(null);
    this.beneficiaryBuildingNameControl.setValue(null);
  }

  private clearBeneficiaryCityFields(): void {
    this.beneficiaryCityControl.setValue(null);
    this.beneficiaryProvinceControl.setValue(null);
    this.beneficiaryPostalCodeControl.setValue(null);
    this.clearBeneficiaryAddressFields();
  }

  private clearBeneficiaryAddressControl(): void {
    this.beneficiaryAddressControl.setValue(null);
  }

  private checkAndAlertBeneficiaryAddressFields(): void {
    this.alertService.alert$.pipe(take(1)).subscribe((currentAlert) => {
      if (this.isBeneficiaryAddressFieldsExpanded && !currentAlert) {
        setTimeout(() => {
          this.alertService.alert({
            messageType: 'key',
            message: this.getDynamicAddressAlert(),
            icon: 'alert-circle',
            type: 'warning'
          });
        });
      }
    });
  }

  private getDynamicAddressAlert(): string {
    const fields = [
      { key: PaymentSepaAdditionalParam.beneficiaryCountry, labelKey: 'payment.receiverInfo.address.country.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryProvince, labelKey: 'payment.receiverInfo.address.province.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryCity, labelKey: 'payment.receiverInfo.address.city.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryStreetName, labelKey: 'payment.receiverInfo.address.street.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryBuildingName, labelKey: 'payment.receiverInfo.address.buildingName.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryBuildingNumber, labelKey: 'payment.receiverInfo.address.buildingNumber.label' },
      { key: PaymentSepaAdditionalParam.beneficiaryPostalCode, labelKey: 'payment.receiverInfo.address.postalCode.label' },
      { key: PaymentSwiftAdditionalParam.beneficiaryAddress, labelKey: 'payment.receiverInfo.address.address.label' }
    ];

    const labels = fields
      .filter((f) => {
        const control = this.form.get(f.key);
        return control && control.validator && control.hasValidator(Validators.required);
      })
      .map((f) => this.translationService.translate(f.labelKey) as string);

    if (!labels.length) return '';
    return this.translationService.translate('payment.receiverInfo.address.addressAlert', {
      fields: labels.join(', ')
    }) as string;
  }

  private initServiceChargeDetails(conditions: PaymentConditions): void {
    if (conditions?.additionalFields?.find(({ name }) => (name as PaymentParam) === PaymentParam.serviceChargeDetails)) {
      this.userCanProvideServiceChargeDetails = true;
      this.serviceChargeDetailsControl.enable();
      const defaultValue = conditions.additionalFields.find(({ name }) => (name as PaymentParam) == PaymentParam.serviceChargeDetails).defaultValue;
      if (!this.payment?.serviceChargeDetails) {
        this.serviceChargeDetailsControl.setValue(defaultValue);
      }
    } else {
      this.userCanProvideServiceChargeDetails = false;
      this.serviceChargeDetailsControl.disable();
    }
  }

  private initInstructionIdentificationControls(): void {
    if (isPaymentSwiftXml(this.paymentConditions?.operationType) || isPaymentTarget2(this.paymentConditions?.operationType)) {
      if (this.paymentConditions?.additionalFields?.find(({ name }) => (name as PaymentSwiftXmlParam) === PaymentSwiftXmlParam.instructionIdentification)) {
        this.userMustProvideInstructionId = true;
        this.instructionIdentificationControl.enable();
        if (!this.instructionIdentificationControl.value) {
          this.instructionIdentificationControl.reset();
        }
      } else {
        this.userMustProvideInstructionId = false;
        this.instructionIdentificationControl.disable();
      }
    } else {
      this.formService.disableFormControls(this.form, Object.values(PaymentSwiftXmlParam));
      this.userMustProvideInstructionId = false;
    }
  }

  private initFieldsMetadata(fieldsMetadata: PaymentFieldMetadata[]): void {
    const mandatoryFields = (fieldsMetadata?.filter((field) => field.mandatory) || []).map((field) => field.name);
    const mandatoryControls = mandatoryFields.map((field) => this.form.get(field)).filter((control) => control !== null);
    this.lastConditionMandatoryControls = [...mandatoryControls];
    if (mandatoryControls.length) {
      this.hasAdditionalInfoControl.setValue(true);
      this.hasAdditionalInfoControl.disable();
      this.beneficiaryAddressFieldsMandatory = mandatoryFields.includes(PaymentSwiftXmlAdditionalParam.beneficiaryPostalCode);
      this.beneficiaryIdentificationMandatory = mandatoryFields.includes(PaymentSepaAdditionalParam.beneficiaryIdentification);
      this.payerIdentificationMandatory = mandatoryFields.includes(PaymentSepaAdditionalParam.payerIdentification);
      this.ultimateBeneficiaryIdentificationMandatory = mandatoryFields.includes(PaymentSepaAdditionalParam.ultimateBeneficiaryIdentification);
      this.collapsed =
        this.appConfig.optionalAdditionalFieldsCollapsed &&
        (this.beneficiaryAddressFieldsMandatory ||
          this.beneficiaryIdentificationMandatory ||
          this.payerIdentificationMandatory ||
          this.ultimateBeneficiaryIdentificationMandatory);
      this.paymentModalService.updateModalClassToExtended();
      this.formService.switchFormControlsRequired(mandatoryControls, true);
    }
  }

  private resetFieldsMetadata() {
    this.collapsed = false;
    this.resetAdditionalInfoSectionManualState();
    this.formService.switchFormControlsRequired(this.lastConditionMandatoryControls, false);
    this.beneficiaryAddressFieldsMandatory = false;
    this.beneficiaryIdentificationMandatory = false;
    this.payerIdentificationMandatory = false;
    this.ultimateBeneficiaryIdentificationMandatory = false;
    this.alertService.cleanUp();
  }

  private initHasAdditionalInfoControl(): void {
    combineLatest([this.operationTypeControl.valueChanges, this.hasAdditionalInfoControl.valueChanges.pipe(startWith(false))])
      .pipe(
        untilDestroyed(this),
        pairwise(),
        filter(([[,], [operationTypeNext]]) => !!operationTypeNext),
        tap(([[operationTypePrev, hasAdditionalInfoPrev], [operationTypeNext, hasAdditionalInfoNext]]) => {
          if (this.beneficiaryNameExtraSubject.getValue()) {
            this.beneficiaryNameExtraSubject.next(null);
          }
          const prevBeneficiaryIdentificationValue = this.beneficiaryCategoryControl.value as string;
          const operationTypeSwitched = operationTypeNext !== this.lastResolvedOperationType;
          this.lastResolvedOperationType = operationTypeNext as PaymentType;
          this.modalService.setStyle(
            hasAdditionalInfoNext
              ? {
                  size: ModalSize.LG,
                  divider: ModalVerticalDivider.half
                }
              : { size: ModalSize.DEFAULT }
          );
          if ((hasAdditionalInfoPrev && !hasAdditionalInfoNext) || (!!operationTypePrev && operationTypePrev !== operationTypeNext)) {
            this.resetAndDisableAdditionalInfoControls();
          }
          if (operationTypeNext !== PaymentType.PAYMENT_GATEWAY && this.gatewayAdditionalControlNames.length > 0) {
            this.formService.disableFormControls(this.form, this.gatewayAdditionalControlNames);
            this.gatewayAdditionalControlNames = [];
            this.gatewayPrefilledFields = [];
          }
          if (
            [
              PaymentType.SEPA_INSTANT,
              PaymentType.SEPA,
              PaymentType.SWIFT_XML,
              PaymentType.TARGET2,
              PaymentType.INTERNAL,
              PaymentType.PAYMENT_GATEWAY
            ].includes(operationTypeNext as PaymentType)
          ) {
            this.formService.enableFormControls(this.form, this.withoutGatewayServerLockedControls(this.identificationMainControlNames));
          }
          if (operationTypeSwitched) {
            this.resetAdditionalControlsOnPaymentTypeSwitch();
          }

          if (hasAdditionalInfoNext) {
            switch (operationTypeNext) {
              case PaymentType.SEPA_INSTANT:
              case PaymentType.SEPA:
                this.formService.switchFormControlsRequired([this.ultimateBeneficiaryNameControl, this.initialPayerNameControl], false);
                this.formService.enableFormControls(this.form, [...this.sepaAdditionalControlNames, ...this.identificationMainControlNames]);
                break;
              case PaymentType.INTERNAL:
                if (this.internalPaymentSepaFields) {
                  this.formService.switchFormControlsRequired([this.ultimateBeneficiaryNameControl, this.initialPayerNameControl], false);
                  this.formService.enableFormControls(this.form, this.sepaAdditionalControlNames);
                } else {
                  this.hasAdditionalInfoControl.setValue(false);
                }
                break;
              case PaymentType.SWIFT:
                this.formService.disableFormControls(this.form, [
                  PaymentSwiftXmlParam.instructionIdentification,
                  ...this.identificationControlNames,
                  ...this.swiftXmlAdditionalControlNames
                ]);
                this.formService.resetFormControls(
                  this.form,
                  [PaymentSwiftXmlParam.instructionIdentification, ...this.swiftXmlAdditionalControlNames].filter(
                    (param) => !this.swiftAdditionalControlNames.includes(param)
                  )
                );
                this.formService.enableFormControls(this.form, this.swiftAdditionalControlNames);
                break;
              case PaymentType.SWIFT_XML:
              case PaymentType.TARGET2:
                this.formService.disableFormControls(this.form, this.swiftAdditionalControlNames);
                this.formService.resetFormControls(
                  this.form,
                  this.swiftAdditionalControlNames.filter((param) => !this.swiftXmlAdditionalControlNames.includes(param))
                );
                this.formService.enableFormControls(this.form, this.swiftXmlAdditionalControlNames);
                if (this.userCanProvideServiceChargeDetails) {
                  this.formService.enableFormControl(this.form, PaymentParam.serviceChargeDetails);
                } else {
                  this.formService.disableFormControl(this.form, PaymentParam.serviceChargeDetails);
                }
                if (prevBeneficiaryIdentificationValue) {
                  this.beneficiaryCategoryControl.setValue(prevBeneficiaryIdentificationValue);
                }
                break;
              case PaymentType.PAYMENT_GATEWAY: {
                const silent = { emitEvent: false };
                this.disableGatewayControls();
                this.formService.disableFormControls(
                  this.form,
                  this.withoutBeneficiaryNameRenderSwitch([
                    ...this.gatewayAdditionalControlNames,
                    ...this.currencyCloudAdditionalControlNames,
                    ...this.sepaAdditionalControlNames,
                    ...this.swiftAdditionalControlNames,
                    ...this.swiftXmlAdditionalControlNames,
                    ...this.identificationControlNames
                  ]),
                  silent
                );
                const gatewayControls = BankTransferGatewayAdditionalControls();
                const paymentGatewayControlsToEnable = this.withoutBeneficiaryNameRenderSwitch(
                  this.withoutGatewayServerLockedControls(this.gatewayAdditionalControlNames)
                );
                this.formService.applyValidatorsAndEnableControls(this.form, gatewayControls, paymentGatewayControlsToEnable, silent);
                this.formService.enableFormControls(
                  this.form,
                  paymentGatewayControlsToEnable.filter((name) => !gatewayControls.some((def) => def.name === name)),
                  silent
                );
                if (prevBeneficiaryIdentificationValue) {
                  this.beneficiaryCategoryControl.setValue(prevBeneficiaryIdentificationValue);
                }
                break;
              }
              case PaymentType.CURRENCY_CLOUD: {
                const currencyCloudControlsToSwitch = this.withoutBeneficiaryNameRenderSwitch(this.currencyCloudAdditionalControlNames);
                this.formService.disableFormControls(this.form, [
                  ...this.gatewayAdditionalControlNames,
                  ...currencyCloudControlsToSwitch,
                  ...this.sepaAdditionalControlNames,
                  ...this.swiftAdditionalControlNames,
                  ...this.swiftXmlAdditionalControlNames
                ]);
                this.formService.enableFormControls(this.form, currencyCloudControlsToSwitch);
                this.formService.switchFormControlsRequired(
                  this.currencyCloudAdditionalControlNames.map((controlName) => this.form.get(controlName)),
                  true
                );
                if (prevBeneficiaryIdentificationValue) {
                  this.beneficiaryCategoryControl.setValue(prevBeneficiaryIdentificationValue);
                }
                break;
              }
              default:
                this.hasAdditionalInfoControl.setValue(false);
            }
          }
        })
      )
      .subscribe();
  }

  private disableGatewayControls(): void {
    const exclude = new Set([
      PaymentGatewayAdditionalParam.customFields,
      PaymentParam.initialAmount,
      PaymentParam.initialCurrency,
      ...this.gatewayAdditionalControlNames
    ]);
    const controlsToDisable = BankTransferGatewayAdditionalControls()
      .map((c) => c.name)
      .filter((name) => !exclude.has(name));
    controlsToDisable.forEach((name) => this.form.get(name)?.disable({ emitEvent: false }));
  }

  private resetAdditionalControlsOnPaymentTypeSwitch(): void {
    this.formService.switchFormControlsRequired(
      this.currencyCloudAdditionalControlNames.map((controlName) => this.form.get(controlName)),
      false
    );
  }

  private initCurrencyCloudAdditionalControls(additionalFieldNames: PaymentAdditionalField[]): void {
    this.currencyCloudAdditionalControlNames = [PaymentCurrencyCloudAdditionalParam.beneficiaryIdentification, ...BENEFICIARY_NAME_PART_FIELDS];
    this.applyBeneficiaryIdentificationNameRequired(additionalFieldNames);
    additionalFieldNames.forEach((controlName) => {
      if (this.currencyCloudAdditionalControlNames.includes(controlName.name)) {
        return;
      }
      const control = BankTransferCurrencyCloudAdditionalControls(this.config).find((control) => control.name == controlName.name);
      if (control) {
        this.currencyCloudAdditionalControlNames.push(control.name);
      }
      if ((controlName.name as PaymentCurrencyCloudAdditionalParam) == PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationCodeType) {
        this.currencyCloudAdditionalControlNames.push(PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationDocument);
      }
      if ((controlName.name as PaymentCurrencyCloudAdditionalParam) == PaymentCurrencyCloudAdditionalParam.beneficiaryId) {
        this.currencyCloudAdditionalControlNames.push(PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationType);
      }
    });
  }

  /** The surname renders both name inputs and `BeneficiaryNameSyncService` owns it, so the payment type must not switch it (DEV-30752). */
  private withoutBeneficiaryNameRenderSwitch(controlNames: string[]): string[] {
    return controlNames.filter((name) => name !== String(PaymentCurrencyCloudAdditionalParam.beneficiaryIdentificationSurname));
  }

  private applyBeneficiaryIdentificationNameRequired(additionalFields: PaymentAdditionalField[]): void {
    BENEFICIARY_NAME_PART_FIELDS.forEach((name) => {
      const control = this.form.get(name);
      if (control) {
        this.formService.switchFormControlRequired(control, !!additionalFields?.find((field) => field.name === name)?.mandatory);
      }
    });
  }

  private initGatewayAdditionalControls(
    conditions: Pick<PaymentConditions, 'operationType' | 'restfulPayPaymentSystem' | 'additionalFields' | 'customFields'>
  ): void {
    const additionalFieldNames = conditions.additionalFields ?? [];
    const customFieldsNames = conditions.customFields;

    this.gatewayAdditionalControlNames = [
      PaymentGatewayAdditionalParam.beneficiaryCategory,
      ...(usesStructuredPaymentFields(conditions) ? [] : BENEFICIARY_NAME_PART_FIELDS)
    ];
    additionalFieldNames.forEach((controlName) => {
      if (this.gatewayAdditionalControlNames.includes(controlName.name)) {
        return;
      }
      const control = BankTransferGatewayAdditionalControls().find((control) => control.name === controlName.name);
      if (control) {
        this.gatewayAdditionalControlNames.push(control.name);
      } else if (this.form.get(controlName.name)) {
        this.gatewayAdditionalControlNames.push(controlName.name);
      } else if (!GATEWAY_RESPONSE_ONLY_FIELDS.includes(controlName.name)) {
        console.warn(`Payment gateway conditions returned unknown field "${controlName.name}" — no matching form control, field will not be rendered`);
      }
    });
    if (customFieldsNames) {
      const customFieldsGroup = this.form.get('customFields') as UntypedFormGroup;
      customFieldsNames.forEach((controlName) => {
        const control = customFieldsGroup.get(controlName.name);
        if (control) {
          control.enable();
        }
      });
    }
  }

  private prefillGatewayCustomFieldsFromPayment(): void {
    const customFieldsGroup = this.form.get(PaymentGatewayAdditionalParam.customFields) as UntypedFormGroup | null;
    if (!customFieldsGroup || !this.payment) {
      return;
    }

    const customFieldValues = this.getPaymentCustomFieldValues();
    const payment = this.payment as unknown as Record<string, unknown>;

    Object.entries(customFieldsGroup.controls).forEach(([name, control]) => {
      const value = customFieldValues[name] ?? payment[name];
      if (value === undefined || value === null || value === '' || this.hasValue(control)) {
        return;
      }
      control.setValue(String(value));
    });
  }

  private getPaymentCustomFieldValues(): Record<string, unknown> {
    const customFields = this.payment?.customFields as unknown;

    if (Array.isArray(customFields)) {
      return Object.fromEntries((customFields as PaymentCustomField[]).map(({ name, value }) => [name, value]));
    }

    if (customFields && typeof customFields === 'object') {
      return customFields as Record<string, unknown>;
    }

    return {};
  }

  private hasValue(control: AbstractControl | null): boolean {
    const value: unknown = control?.value;
    return value !== null && value !== undefined && value !== '';
  }

  private initCurrencyCloudCustomControls(customFields: { name: string; label: string; type: string }[]): void {
    this.form.registerControl(PaymentCurrencyCloudAdditionalParam.customFields, new UntypedFormGroup({}));
    this.formService.registerFormControls(
      this.customFields,
      customFields.map((customField) => {
        this.currencyCloudCustomControlNames.push(customField.name);
        return {
          name: customField.name,
          control: new UntypedFormControl(
            {
              value: this.getPaymentCustomFieldValue(customField.name),
              disabled: false
            },
            [Validators.required]
          )
        };
      })
    );
  }

  private initIdentificationControlValueChanges(): void {
    const initialPayerControlNames = [...this.initialPayerIdentificationControlNames, ...this.initialPayerAddressControlNames];
    const ultimateBeneficiaryControlNames = [...this.ultimateBeneficiaryIdentificationControlNames, ...this.ultimateBeneficiaryAddressControlNames];
    const updateInitialPayerNameRequired = () => this.updateIdentificationNameRequired(this.initialPayerNameControl, initialPayerControlNames);
    const updateUltimateBeneficiaryNameRequired = () =>
      this.updateIdentificationNameRequired(this.ultimateBeneficiaryNameControl, ultimateBeneficiaryControlNames);

    this.registerIdentificationNameRequiredValueChanges(initialPayerControlNames, updateInitialPayerNameRequired);
    this.registerIdentificationNameRequiredValueChanges(ultimateBeneficiaryControlNames, updateUltimateBeneficiaryNameRequired);

    this.operationTypeControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
      updateInitialPayerNameRequired();
      updateUltimateBeneficiaryNameRequired();
    });

    this.hasAdditionalInfoControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => {
      updateInitialPayerNameRequired();
      updateUltimateBeneficiaryNameRequired();
    });

    updateInitialPayerNameRequired();
    updateUltimateBeneficiaryNameRequired();
  }

  private registerIdentificationNameRequiredValueChanges(controlNames: string[], update: () => void): void {
    controlNames.forEach((controlName) => {
      const control = this.form.get(controlName);
      if (control) {
        control.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(update);
      }
    });
  }

  private updateIdentificationNameRequired(nameControl: AbstractControl, controlNames: string[]): void {
    const hasIdentificationValue = controlNames.some((controlName) => this.form.get(controlName)?.value);
    this.formService.switchFormControlsRequired([nameControl], hasIdentificationValue);
  }

  private resetAndDisableAdditionalInfoControls(): void {
    this.resetAdditionalInfoSectionManualState();
    this.formService.resetFormControls(this.form, this.sepaAdditionalControlNames);
    this.formService.disableFormControls(this.form, this.sepaAdditionalControlNames);

    this.formService.resetFormControls(this.form, this.swiftAdditionalControlNames);
    this.formService.disableFormControls(this.form, this.swiftAdditionalControlNames);

    this.formService.resetFormControls(this.form, this.swiftXmlAdditionalControlNames);
    this.formService.disableFormControls(this.form, this.swiftXmlAdditionalControlNames);

    this.formService.resetFormControls(this.form, this.currencyCloudAdditionalControlNames);
    this.formService.disableFormControls(this.form, this.currencyCloudAdditionalControlNames);

    this.formService.resetFormControls(this.form, this.gatewayAdditionalControlNames);
    this.formService.disableFormControls(this.form, this.gatewayAdditionalControlNames);

    this.formService.resetFormControls(this.form, BENEFICIARY_NAME_PART_FIELDS);
    this.formService.disableFormControls(this.form, BENEFICIARY_NAME_PART_FIELDS);

    this.formService.resetFormControls(this.form, this.identificationControlNames);
    this.formService.disableFormControls(this.form, this.identificationControlNames);

    if (this.customFields) {
      this.formService.resetFormControls(this.customFields, this.currencyCloudCustomControlNames);
      this.formService.disableFormControls(this.customFields, this.currencyCloudCustomControlNames);
      this.formService.resetFormControls(this.form, ['customFields']);
      this.formService.disableFormControls(this.form, ['customFields']);
    }

    this.sendPayerAddressControl.reset(false); // default value - false
  }

  private syncGatewayCustomFieldValuesWithPaymentConditions(): void {
    if (!this.paymentConditions?.customFields?.length) {
      return;
    }

    const customFieldsGroup = this.form.get(PaymentGatewayAdditionalParam.customFields) as FormGroup | null;
    if (!customFieldsGroup) {
      return;
    }

    this.paymentConditions.customFields.forEach((customField) => {
      const value = customFieldsGroup.get(customField.name)?.value as unknown;
      customField.value = value == null ? undefined : String(value);
    });
  }

  private getPaymentCustomFieldValue(fieldName: string): string | null {
    if (!this.payment?.customFields) {
      return null;
    }

    if (Array.isArray(this.payment.customFields)) {
      return this.payment.customFields.find((paymentCustomField) => paymentCustomField?.name === fieldName)?.value ?? null;
    }

    const value = (this.payment.customFields as Record<string, unknown>)[fieldName];
    return value == null ? null : String(value);
  }

  /**
   * `GET /conditions` needs the category even while its control is disabled, but `POST /payments`
   * must not have it: unpicked, it arrives without the identification fields the backend requires.
   */
  private formValueToConditionsPayload(): Payment {
    const payment = this.formValueToPayment();

    if (this.beneficiaryCategoryControl.value) {
      payment.beneficiaryCategory = this.beneficiaryCategoryControl.value as string;
    }

    return payment;
  }

  private formValueToPayment(): Payment {
    let payment: Payment = this.form.value as Payment;

    if (isPaymentGateway(payment.operationType)) {
      payment = this.resolveOtherFieldValues(payment);
      payment = this.sendOnlySelectedIdentificationFields(payment);
    }

    if (this.beneficiaryBankCountryControl.value) {
      payment.beneficiaryBankCountry = this.beneficiaryBankCountryControl.value as string;
    }

    if (this.beneficiaryBankIdentifierControl.value) {
      payment.beneficiaryBankIdentifier = this.beneficiaryBankIdentifierControl.value as string;
    }

    if (this.storedPayeeVerificationId) {
      payment.payeeVerificationId = this.storedPayeeVerificationId;
    }

    if (this.vopEnabled) {
      payment.acceptPayeeVerificationMismatch = !!this.userAgreementControl.value;
    }

    payment.editStandingOrderId = this?.payment?.editStandingOrderId;
    payment = this.addInstitutionDisabledInputValues(
      payment,
      PaymentInternationalParam.beneficiaryBankIdentifier,
      PaymentInternationalParam.beneficiaryBankName,
      PaymentInternationalParam.beneficiaryBankAddress,
      PaymentInternationalParam.beneficiaryBankCountry,
      PaymentSwiftXmlParam.instructionIdentification
    );
    payment = this.addInstitutionDisabledInputValues(
      payment,
      PaymentSwiftAdditionalParam.correspondentBankCode,
      PaymentSwiftAdditionalParam.correspondentBankName,
      PaymentSwiftAdditionalParam.correspondentBankAddress,
      PaymentSwiftXmlAdditionalParam.correspondentBankCountry
    );
    payment.attachments = this.attachments;
    payment.beneficiaryAccountNumber = this.beneficiaryAccountNumberControl.value as string;
    payment.beneficiaryName = this.beneficiaryNameControl.value as string;

    return payment;
  }

  private sendOnlySelectedIdentificationFields(payment: Payment): Payment {
    const updatedPayment = { ...payment };
    const identificationField = String(PaymentSepaAdditionalParam.payerIdentification);
    const inPlay = isIdentificationInPlay('payer', this.gatewayAdditionalControlNames);
    const identificationValue = this.gatewayControlValue(identificationField);

    const isSent = (name: string): boolean => {
      if (!inPlay || name === identificationField) {
        return true;
      }
      return identificationValue !== null && isShownForSelectedIdentification(name, this.form);
    };

    PAYER_IDENTIFICATION_FIELDS.forEach((name) => {
      if (!isSent(name)) {
        delete updatedPayment[name];
        return;
      }

      const value = this.gatewayControlValue(name);
      if (value !== null) {
        updatedPayment[name] = value;
      }
    });

    return this.dropUnchosenBeneficiaryIdentification(updatedPayment);
  }

  private dropUnchosenBeneficiaryIdentification(payment: Payment): Payment {
    if (!isIdentificationInPlay('beneficiary', this.gatewayAdditionalControlNames)) {
      return payment;
    }

    const updatedPayment = { ...payment };
    const chosen = this.gatewayControlValue(PaymentSepaAdditionalParam.beneficiaryIdentificationType) !== null;

    BENEFICIARY_IDENTIFICATION_FIELDS.forEach((name) => {
      if (!chosen || !isShownForSelectedIdentification(name, this.form)) {
        delete updatedPayment[name];
      }
    });

    return updatedPayment;
  }

  private gatewayControlValue(name: string): unknown {
    const value = this.form.get(name)?.value as unknown;
    return value === undefined || value === '' ? null : value;
  }

  private resolveOtherFieldValues(payment: Payment): Payment {
    const updatedPayment = { ...payment };

    Object.keys(this.form.controls).forEach((key) => {
      if (this.form.get(key)?.value === 'OTHER') {
        const otherValue = this.form.get(`${key}_other`)?.value as string;
        if (otherValue) {
          updatedPayment[key] = otherValue;
        }
        delete updatedPayment[`${key}_other`];
      }
    });

    return updatedPayment;
  }

  private setPaymentDetailsValidators(operationType: PaymentType | null | undefined): void {
    this.paymentDetailsControl.setValidators(
      isPaymentBoa(operationType)
        ? [Validators.maxLength(this.config.detailsMaxLength)]
        : [Validators.required, Validators.maxLength(this.config.detailsMaxLength)]
    );
  }

  private addInstitutionDisabledInputValues(
    payment: Payment,
    institutionIdentifierControlName: string,
    institutionNameControlName: string,
    institutionAddressControlName: string,
    institutionCountryControlName?: string,
    institutionInstructionIdControlName?: string
  ): Payment {
    if (this.form.get(institutionIdentifierControlName)?.value) {
      const institutionName = this.form.get(institutionNameControlName)?.value as string;
      const institutionAddress = this.form.get(institutionAddressControlName)?.value as string;
      const institutionCountry = this.form.get(institutionCountryControlName)?.value as string;
      const institutionInstructionId = this.form.get(institutionInstructionIdControlName)?.value as string;

      return {
        ...payment,
        ...(institutionName && { [institutionNameControlName]: institutionName }),
        ...(institutionAddress && { [institutionAddressControlName]: institutionAddress }),
        ...(institutionCountry && { [institutionCountryControlName]: institutionCountry }),
        ...(institutionInstructionId && { [institutionInstructionIdControlName]: institutionInstructionId })
      };
    }
    return payment;
  }

  hasValueInFields(form: FormGroup, controlNames: string[]): boolean {
    return controlNames.some((controlName) => {
      const control = form.get(controlName);
      return !!control.value || control.dirty;
    });
  }

  isAdditionalInfoSectionActive(section: string, controlNames: string[], forceExpanded = false): boolean {
    if (!this.collapsed || forceExpanded || this.hasVisibleValidationIssuesInFields(controlNames)) {
      return true;
    }

    const manualState = this.additionalInfoSectionManualState[section];
    if (manualState !== undefined) {
      return manualState;
    }

    return this.hasNonEmptyValuesInFields(controlNames);
  }

  onAdditionalInfoSectionActiveChange(section: string, active: boolean): void {
    if (!this.collapsed) {
      return;
    }

    this.additionalInfoSectionManualState[section] = active;
  }

  private hasNonEmptyValuesInFields(controlNames: string[]): boolean {
    return controlNames.some((controlName) => isControlFilled(this.form.get(controlName)));
  }

  private hasVisibleValidationIssuesInFields(controlNames: string[]): boolean {
    return controlNames.some((controlName) => {
      const control = this.form.get(controlName);
      return !!control?.enabled && control.invalid && (control.touched || control.dirty || isControlFilled(control));
    });
  }

  private resetAdditionalInfoSectionManualState(): void {
    this.additionalInfoSectionManualState = {};
  }

  fileUploading(uploading: boolean): void {
    this.filesUploading = uploading;
  }

  formHasApiError(): boolean {
    return this.formService.formHasApiError(this.form);
  }

  markAllAsTouched() {
    Object.keys(this.form.controls).forEach((field) => {
      const control = this.form.get(field);
      if (control?.value) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }
  isSubmitButtonDisabled(submitButtonDisabled: boolean | null): boolean {
    const agreementRequired = this.showUserAgreementAlert && !this.alertAgreementControl.value;
    return (
      !this.paymentConditions ||
      submitButtonDisabled ||
      (!this.form.valid && !this.formHasApiError()) ||
      (this.payment?.editStandingOrderId && this.form.pristine && !this.showUserAgreementAlert) ||
      this.filesUploading ||
      this.addressInvalid ||
      agreementRequired
    );
  }

  isSaveButtonDisabled(saveButtonDisabled: boolean | null): boolean {
    const agreementRequired = this.showUserAgreementAlert && !this.alertAgreementControl.value;
    return (
      !this.paymentConditions ||
      saveButtonDisabled ||
      (!this.form.valid && !this.formHasApiError()) ||
      this.filesUploading ||
      this.addressInvalid ||
      agreementRequired
    );
  }

  private get vopEnabled(): boolean {
    return this.appConfig.vopEnabled && !this.userProfile?.vopOptOut;
  }

  get alertAgreementControl(): AbstractControl {
    return this.closeMatchAgreementRequired ? this.closeMatchAgreementControl : this.userAgreementControl;
  }

  get payeeVerificationInlineMessageKey(): string {
    const currentState = this.payeeVerificationInlineStateSubject.value;
    switch (currentState) {
      case PayeeVerificationInlineState.VALIDATING:
        return 'payment.verificationOfPayee.validatingTitle';
      case PayeeVerificationInlineState.TAKING_LONGER:
        return 'payment.verificationOfPayee.takingLongerThanExpectedTitle';
      default:
        return '';
    }
  }

  get operationTypeControl(): AbstractControl {
    return this.form.get(PaymentParam.operationType);
  }

  get paymentType(): PaymentType | null {
    return this.operationTypeControl?.value as PaymentType;
  }

  get beneficiaryNameControl(): UntypedFormControl {
    return this.form.get(PaymentParam.beneficiaryName) as UntypedFormControl;
  }

  get payerAccountControl(): FormControl<Account | null> {
    return this.form.get(PaymentParam.payerAccount) as FormControl<Account | null>;
  }

  get waitForFundsControl(): UntypedFormControl {
    return this.form.get(PaymentParam.waitForFunds) as UntypedFormControl;
  }

  get feeAccountControl(): UntypedFormControl {
    return this.form.get(PaymentParam.feeAccount) as UntypedFormControl;
  }

  get feeAccountNumberControl(): UntypedFormControl {
    return this.form.get(PaymentParam.feeAccountNumber) as UntypedFormControl;
  }

  get feeAccountCurrencyControl(): UntypedFormControl {
    return this.form.get(PaymentParam.feeAccountCurrency) as UntypedFormControl;
  }

  get customFields(): UntypedFormGroup {
    return this.form.get(PaymentCurrencyCloudAdditionalParam.customFields) as UntypedFormGroup;
  }

  get waitForFunds(): boolean {
    return this.waitForFundsControl.value as boolean;
  }

  get payerAccountNumberControl(): AbstractControl {
    return this.form.get(PaymentParam.payerAccountNumber);
  }

  get beneficiaryAccountNumberControl(): AbstractControl {
    return this.form.get(PaymentParam.beneficiaryAccountNumber);
  }

  get beneficiaryIdentification(): AbstractControl {
    return this.form.get(PaymentCurrencyCloudAdditionalParam.beneficiaryIdentification);
  }

  get beneficiaryCategory(): AbstractControl {
    return this.form.get(PaymentGatewayAdditionalParam.beneficiaryCategory);
  }

  get beneficiaryCityControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryCity);
  }

  get beneficiaryCountryControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryCountry);
  }

  get beneficiaryProvinceControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryProvince);
  }

  get beneficiaryStreetNameControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryStreetName);
  }

  get beneficiaryBuildingNumberControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryBuildingNumber);
  }

  get beneficiaryBuildingNameControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryBuildingName);
  }

  get beneficiaryPostalCodeControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.beneficiaryPostalCode);
  }

  get beneficiaryBankIdentifierControl(): AbstractControl {
    return this.form.get(PaymentInternationalParam.beneficiaryBankIdentifier);
  }

  get beneficiaryBankNameControl(): AbstractControl {
    return this.form.get(PaymentInternationalParam.beneficiaryBankName);
  }

  get beneficiaryBankAddressControl(): AbstractControl {
    return this.form.get(PaymentInternationalParam.beneficiaryBankAddress);
  }

  get beneficiaryBankCountryControl(): AbstractControl {
    return this.form.get(PaymentInternationalParam.beneficiaryBankCountry);
  }

  get instructionIdentificationControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlParam.instructionIdentification);
  }

  get serviceChargeDetailsControl(): AbstractControl {
    return this.form.get(PaymentParam.serviceChargeDetails);
  }

  get additionalBeneficiaryBankCodeTypeControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCodeType);
  }

  get additionalBeneficiaryBankCodeControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.additionalBeneficiaryBankCode);
  }

  get additionalBeneficiaryCorrespondentBankCodeTypeControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCodeType);
  }

  get additionalBeneficiaryCorrespondentBankCodeControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.additionalBeneficiaryCorrespondentBankCode);
  }

  get correspondentBankCodeControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.correspondentBankCode);
  }

  get correspondentBankNameControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.correspondentBankName);
  }

  get correspondentBankAddressControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.correspondentBankAddress);
  }

  get correspondentBankCountryControl(): AbstractControl {
    return this.form.get(PaymentSwiftXmlAdditionalParam.correspondentBankCountry);
  }

  get initialPayerIdentificationControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.initialPayerIdentification);
  }

  get initialPayerNameControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.initialPayerName);
  }

  get ultimateBeneficiaryIdentificationControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.ultimateBeneficiaryIdentification);
  }

  get ultimateBeneficiaryNameControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.ultimateBeneficiaryName);
  }

  get beneficiaryAddressControl(): AbstractControl {
    return this.form.get(PaymentSwiftAdditionalParam.beneficiaryAddress);
  }

  get amountControl(): AbstractControl {
    return this.form.get(PaymentParam.amount);
  }

  get currencyControl(): AbstractControl {
    return this.form.get(PaymentParam.currency);
  }

  get beneficiaryCategoryControl(): AbstractControl {
    return this.form.get(PaymentGatewayAdditionalParam.beneficiaryCategory);
  }

  get initialAmountControl(): AbstractControl {
    return this.form.get(PaymentParam.initialAmount);
  }

  get initialCurrencyControl(): AbstractControl {
    return this.form.get(PaymentParam.initialCurrency);
  }

  get initialAmount(): number {
    return this.initialAmountControl?.value as number;
  }

  get initialCurrency(): string {
    return this.initialCurrencyControl?.value as string;
  }

  get amount(): number {
    return this.amountControl?.value as number;
  }

  get currency(): string {
    return this.currencyControl?.value as string;
  }

  get paymentDetailsTypeControl(): AbstractControl {
    return this.form.get(PaymentParam.paymentDetailsType);
  }

  get paymentDetailsControl(): AbstractControl {
    return this.form.get(PaymentParam.paymentDetails);
  }

  get paymentCodeControl(): AbstractControl {
    return this.form.get(PaymentParam.paymentCode);
  }

  get hasAdditionalInfoControl(): AbstractControl {
    return this.form.get(PaymentParam.hasAdditionalInfo);
  }

  get sendPayerAddressControl(): AbstractControl {
    return this.form.get(PaymentSepaAdditionalParam.sendPayerAddress);
  }

  get scheduledControl(): AbstractControl {
    return this.form.get(PaymentParam.scheduled);
  }

  get scheduledFrequencyControl(): AbstractControl {
    return this.form.get(PaymentScheduledParam.scheduledFrequency);
  }

  get scheduledStartDateControl(): AbstractControl {
    return this.form.get(PaymentScheduledParam.scheduledStartDate);
  }

  get isPaymentSwift(): boolean {
    return isPaymentSwift(this.paymentType);
  }

  get isPaymentSwiftXml(): boolean {
    return isPaymentSwiftXml(this.paymentType);
  }

  get isPaymentInternal(): boolean {
    return isPaymentInternal(this.paymentType);
  }

  get isPaymentTarget2(): boolean {
    return isPaymentTarget2(this.paymentType);
  }

  get isPaymentSepa(): boolean {
    return isPaymentSepa(this.paymentType);
  }

  get isPaymentCurrencyCloud(): boolean {
    return isPaymentCurrencyCloud(this.paymentType);
  }

  get isPaymentGateway(): boolean {
    return isPaymentGateway(this.paymentType);
  }

  get canSelectGatewayCurrency(): boolean {
    return !this.scheduled && !this.isPaymentInternal && this.isPaymentGatewayCurrencySelectionAllowed(this.payerAccountControl.value);
  }

  get canHaveAdditionalInfo(): boolean {
    return (
      this.isPaymentSwift ||
      this.isPaymentSwiftXml ||
      this.isPaymentSepa ||
      this.isPaymentCurrencyCloud ||
      this.isPaymentTarget2 ||
      this.internalPaymentSepaFields ||
      this.isPaymentGateway
    );
  }

  get hasAdditionalInfo(): boolean {
    return this.hasAdditionalInfoControl?.value as boolean;
  }

  get swiftPaymentBicNamingAndPostalCodeInfoEnabled(): boolean {
    return !!this.appConfig.swiftPaymentBicNamingAndPostalCodeInfoEnabled && (this.isPaymentSwiftXml || this.isPaymentTarget2);
  }

  get displaySepaFields(): boolean {
    return this.isPaymentSepa || this.internalPaymentSepaFields;
  }

  get internalPaymentSepaFields(): boolean {
    return this.isPaymentInternal && this.appConfig.internalPaymentSepaFields;
  }

  get scheduled(): boolean {
    return this.scheduledControl?.value as boolean;
  }

  get scheduledFrequency(): ScheduledPaymentPeriodType {
    return this.scheduledFrequencyControl?.value as ScheduledPaymentPeriodType;
  }

  get isInstructionIdentificationEnabled(): boolean {
    return this.userMustProvideInstructionId && (this.isPaymentSwiftXml || this.isPaymentTarget2);
  }

  get isBeneficiaryAddressFieldsExpanded(): boolean {
    return this.collapsed && this.beneficiaryAddressFieldsMandatory;
  }

  get beneficiaryNameExtra$() {
    return this.beneficiaryNameExtraSubject.asObservable();
  }
}
